import { api, API_BASE } from '../services/api';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { generatePDF as generatePDFUtil } from '../utils/pdfGenerator';
import '../pages/StaffDashboard.css';
import UploadDocumentsSection from '../components/UploadDocumentsSection';
import '../components/UploadDocuments.css';
import TwoFactorSetup from '../components/TwoFactorSetup';
// React Icons
import {
  FaPlusCircle,
  FaClipboardList,
  FaSearch,
  FaUpload,
  FaHome,
  FaSpinner,
  FaBars,
  FaTimes,

  FaSignOutAlt,
  FaTasks,
} from 'react-icons/fa';
import { ChevronDown } from 'lucide-react';

import logo from "../assets/business-logo.png";
import businessLogo from '../assets/business-logo.png';
import { aiGradeFromFaults, aiSummarizeAppraisal } from "../services/ai";

import TyreOverlaySVG from "../components/TyreOverlaySVG";

// Lottie
import Lottie from "lottie-react";
import sunnyAnim from "../assets/lottie/sunny.json";
import cloudyAnim from "../assets/lottie/cloudy.json";
import rainAnim from "../assets/lottie/rain.json";
import snowAnim from "../assets/lottie/snow.json";
import fogAnim from "../assets/lottie/fog.json";
import thunderstormAnim from "../assets/lottie/thunderstorm.json";

// Components
import ExteriorMap from '../components/ExteriorMap';
import InteriorMap from '../components/InteriorMap';
import FaultTable from '../components/FaultTable';
import ProgressBar from '../components/ProgressBar';

// Static images
import exteriorMobileImg from '../assets/exterior mobile.png';
import interiorMobileImg from '../assets/interior mobile.png';

// Mobile Interior Section Images


const StaffDashboard = () => {
  const getGreetingLabel = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good Morning";
    if (h < 17) return "Good Afternoon";
    return "Good Evening";
  };

  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  // ✅ Fetch user on mount handled by existing effect below
  // (Removed redundant debug effect)

  const [activeSection, setActiveSection] = useState('dashboard');
  const [selectedRegNumber, setSelectedRegNumber] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [pdfFor, setPdfFor] = useState(null);

  const [uploadedDocuments, setUploadedDocuments] = useState([]); // ✅ NEW: Store all documents at parent level

  const [activeTab, setActiveTab] = useState('details');
  const userMenuRef = useRef(null);

  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const displayName = user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : user?.username || "Staff";
  const initials = (displayName || "S")
    .split(" ").filter(Boolean).map(s => s[0]).slice(0, 2).join("").toUpperCase();

  // Weather state
  const [weather, setWeather] = useState({
    loading: true,
    location: '—',
    tempC: null,
    desc: '—',
    icon: '⛅',
    lottieAnim: null
  });

  const codeToWeather = (code) => {
    const map = {
      0: ['Clear sky', '☀️', sunnyAnim],
      1: ['Mainly clear', '🌤️', sunnyAnim],
      2: ['Partly cloudy', '⛅', cloudyAnim],
      3: ['Overcast', '☁️', cloudyAnim],
      45: ['Fog', '🌫️', fogAnim],
      48: ['Depositing rime fog', '🌫️', fogAnim],
      51: ['Light drizzle', '🌦️', rainAnim],
      53: ['Drizzle', '🌦️', rainAnim],
      55: ['Heavy drizzle', '🌧️', rainAnim],
      61: ['Light rain', '🌦️', rainAnim],
      63: ['Rain', '🌧️', rainAnim],
      65: ['Heavy rain', '🌧️', rainAnim],
      71: ['Light snow', '🌨️', snowAnim],
      73: ['Snow', '🌨️', snowAnim],
      75: ['Heavy snow', '❄️', snowAnim],
      80: ['Rain showers', '🌦️', rainAnim],
      81: ['Rain showers', '🌦️', rainAnim],
      82: ['Heavy showers', '🌧️', rainAnim],
      95: ['Thunderstorm', '⛈️', thunderstormAnim],
      96: ['Thunderstorm', '⛈️', thunderstormAnim],
      99: ['Thunderstorm', '⛈️', thunderstormAnim],
    };
    return map[code] || ['Weather', '🌡️', null];
  };

  // Reverse geocoding
  const reverseLookupName = async (lat, lon) => {
    try {
      const r = await fetch(`${API_BASE}/api/geocode?lat=${lat}&lon=${lon}`);
      const j = await r.json();
      return j?.address?.suburb || j?.address?.neighbourhood || j?.address?.town || j?.address?.city || j?.address?.village || 'Your Location';
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const getWeather = async (lat, lon, label) => {
      try {
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true&timezone=auto`;
        const res = await fetch(url);
        const data = await res.json();
        const cw = data.current_weather;
        const [desc, icon, lottieAnim] = codeToWeather(cw.weathercode);
        setWeather({
          loading: false,
          location: label,
          tempC: Math.round(cw.temperature),
          desc,
          icon,
          lottieAnim
        });
      } catch {
        setWeather(w => ({ ...w, loading: false, desc: 'Unavailable', icon: '🌍', lottieAnim: null }));
      }
    };

    const fallbackLat = 51.510;
    const fallbackLon = -0.420;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          const name = await reverseLookupName(latitude, longitude);
          const label = name || 'HAYES';
          await getWeather(latitude, longitude, label);
        },
        async () => {
          const label = 'HAYES';
          await getWeather(fallbackLat, fallbackLon, label);
        },
        { timeout: 5000 }
      );
    } else {
      const label = 'HAYES';
      getWeather(fallbackLat, fallbackLon, label);
    }
  }, []);

  useEffect(() => {
    const body = document.body;
    if (sidebarOpen) body.classList.add('no-scroll');
    else body.classList.remove('no-scroll');
    return () => body.classList.remove('no-scroll');
  }, [sidebarOpen]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const notificationRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (!userMenuRef.current?.contains(e.target)) setUserMenuOpen(false);
      if (!notificationRef.current?.contains(e.target)) setShowNotifications(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setUserMenuOpen(false);
        setShowNotifications(false);
      }
    };
    window.addEventListener("click", onClick);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const [currentStep, setCurrentStep] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [viewedNotifications, setViewedNotifications] = useState([]);
  const [vehicleData, setVehicleData] = useState(null);
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const [message, setMessage] = useState('');
  const [showRefreshWarning, setShowRefreshWarning] = useState(false);



  const createEmptyTyre = (position) => ({
    position,
    treadDepth: '',
    condition: ''
  });
  const [filterStatus, setFilterStatus] = useState(null); // Add this line
  const initialForm = {
    reg: '', vin: '', ownerName: '', ownerContact: '', customerAddress: '', customerContact: '',
    make: '', model: '', year: '', colour: '', transmission: '',
    mileage: '', date: '', fuelType: '', engineSize: '', co2: '', euroStatus: '', regDate: '',
    artEndDate: '', motStatus: '', revenueWeight: '', taxDueDate: '', taxStatus: '', wheelplan: '',
    yearOfManufacture: '', dateOfLastV5CIssued: '',
    vehiclePhoto: null,
    overallGrade: '',
    conditionMeter: 50,
    // Step 6 ke naye fields add karein:
    vehicleMileage: '',
    mileageModifications: null, // 'yes' ya 'no'
    hasV5Document: null, // 'yes' ya 'no'
    numberOfOwners: '', // dropdown value
    numberOfKeys: '', // '1', '2', ya '3+'
    firstRegisteredUK: null, // 'yes' ya 'no'
    insuranceWriteOff: null, // 'yes' ya 'no' with dropdown
    insuranceWriteOffType: '', // agar 'yes' hai to category

    serviceHistoryType: '', // 'Full', 'Partial', 'None'
    serviceHistoryCount: '', // kitne records
    mainDealer: null, // 'yes' ya 'no'
    vehicleUsageHistory: '', // 'None', 'Private Hire', 'Taxi', etc.

    motStatus: '', // already hai but keep it
    motExpiryDate: '',
    additionalNotes: '',
    tyres: [
      createEmptyTyre('Front Left'),
      createEmptyTyre('Front Right'),
      createEmptyTyre('Rear Left'),
      createEmptyTyre('Rear Right')
    ],
    lightsCheck: false, mirrorsCheck: false, wipersCheck: false,
    engineStartSmooth: false, steeringAlignment: false, brakePerformance: false, gearShiftQuality: false,
    testDriveNotes: '',
    linkedTaskId: null, // Link appraisal to a job task
    deviceType: 'desktop', // 'desktop' or 'mobile' - determines PDF layout
  };

  const [form, setForm] = useState(initialForm);
  const [exteriorFaults, setExteriorFaults] = useState([]);
  const [interiorFaults, setInteriorFaults] = useState([]);

  const [editFaultIndex, setEditFaultIndex] = useState(null);
  const [editFaultType, setEditFaultType] = useState(null);
  const [appraisals, setAppraisals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showManualPopup, setShowManualPopup] = useState(false);

  // Step 6 Popup Modal States
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [showServicePopup, setShowServicePopup] = useState(false);
  const [showMechanicalPopup, setShowMechanicalPopup] = useState(false); // Step 5 mechanical checks popup

  // Mobile fullscreen map modal state
  const [mobileFullscreenMap, setMobileFullscreenMap] = useState(null); // 'exterior' or 'interior' or null

  // ======= TASKS STATE =======
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [viewedTaskNotifications, setViewedTaskNotifications] = useState(() => {
    const saved = localStorage.getItem(`viewedTaskNotifications_${user?.username}`);
    return saved ? JSON.parse(saved) : [];
  });

  const loadTasks = async () => {
    if (!user?.id) return;
    setTasksLoading(true);
    try {
      const data = await api.getStaffTasks(user.id);
      setTasks(data || []);
    } catch (e) {
      console.error("Failed to load tasks", e);
    } finally {
      setTasksLoading(false);
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await api.updateTask(taskId, { status: newStatus });
      setTasks(prev => prev.map(t => t._id === taskId ? { ...t, status: newStatus } : t));
      // Only show message if NOT starting a job (because start job switches view)
      if (newStatus !== 'In Progress') {
        setMessage(`✅ Task marked as ${newStatus}`);
        setTimeout(() => setMessage(""), 3000);
      }
    } catch (e) {
      setMessage("❌ Failed to update task status");
      setTimeout(() => setMessage(""), 3000);
    }
  };

  const handleStartJob = async (task) => {
    // 1. Mark task as In Progress
    await handleUpdateTaskStatus(task._id, 'In Progress');

    // 2. Reset appraisal form (but don't clear faults yet)
    startNewAppraisal();

    // 3. Pre-fill Reg Num and Link Task
    setForm(prev => ({
      ...prev,
      reg: (task.title || '').toUpperCase().replace(/\s+/g, ''), // Convert to uppercase and remove spaces
      linkedTaskId: task._id,
      customerAddress: task.customerAddress || '',
      customerContact: task.customerContact || ''
    }));

    // 4. Switch to New Appraisal tab
    setActiveSection('start');

    // 5. Load previous faults from task AFTER switching to appraisal view
    // This ensures they don't get cleared by startNewAppraisal
    setTimeout(() => {
      if (task.previousReport && task.previousReport.extractedFaults && task.previousReport.extractedFaults.length > 0) {
        const previousFaults = task.previousReport.extractedFaults;

        console.log('📋 Loading previous faults:', previousFaults);

        // Helper function to map severity to grading-compatible detail format
        const mapSeverityToDetail = (severity, damage) => {
          const sev = (severity || '').toLowerCase();
          const dmg = (damage || '').toLowerCase();

          // Map severity levels to NAMA grading criteria
          if (sev === 'major') {
            if (dmg.includes('scratch') || dmg.includes('scuff')) {
              return 'Over 25mm Thru Paint'; // Major paint damage
            }
            if (dmg.includes('dent')) {
              return 'Over 30mm'; // Major dent
            }
            if (dmg.includes('crack')) {
              return 'Over 30% Of Panel'; // Major crack
            }
            return 'Replace'; // Default major = needs replacement
          }

          if (sev === 'moderate') {
            if (dmg.includes('scratch') || dmg.includes('scuff')) {
              return 'UpTo 25mm Thru Paint'; // Moderate paint
            }
            if (dmg.includes('dent')) {
              return 'Between 10mm + 30mm'; // Moderate dent
            }
            return 'UpTo 25mm'; // Default moderate
          }

          // Minor or unknown severity
          if (dmg.includes('scratch') || dmg.includes('scuff')) {
            return 'UpTo 25mm Not Thru Paint'; // Minor paint
          }
          if (dmg.includes('dent')) {
            return 'UpTo 10mm'; // Minor dent
          }

          return severity || 'Minor'; // Fallback
        };

        // Transform faults to match the expected structure
        // Backend sends: { x, y, type, part, damage, severity, ... }
        // Maps expect: { coords: { x, y }, part, damage, detail, ... }
        const transformedFaults = previousFaults.map((f, index) => {
          // Use AI-mapped coordinates from backend
          const x = f.x || 150;  // Fallback to center if no coords
          const y = f.y || 150;

          // Capitalize damage for consistency with manual faults
          const capitalizedDamage = (f.damage || '').charAt(0).toUpperCase() + (f.damage || '').slice(1);

          return {
            coords: { x, y },  // Use backend AI-mapped coordinates
            part: f.part || 'Unknown',
            damage: capitalizedDamage,  // "Scratch", "Dent", etc.
            detail: mapSeverityToDetail(f.severity, f.damage),  // Map to grading format
            description: f.description || '',
            fromPrevious: true,
            sectionId: f.sectionId, // ✅ Ensure sectionId is passed for mobile view
            idx: index + 1
          };
        });

        // Separate faults by type
        const prevExterior = transformedFaults.filter((_, i) => previousFaults[i].type === 'exterior');
        const prevInterior = transformedFaults.filter((_, i) => previousFaults[i].type === 'interior');

        console.log(`📍 Exterior faults: ${prevExterior.length}, Interior faults: ${prevInterior.length}`);
        console.log('Transformed exterior faults:', prevExterior);
        console.log('Transformed interior faults:', prevInterior);

        // Set faults with fromPrevious flag
        setExteriorFaults(prevExterior);
        setInteriorFaults(prevInterior);

        setMessage(`✅ Loaded ${previousFaults.length} previous fault${previousFaults.length !== 1 ? 's' : ''} from PDF`);
        setTimeout(() => setMessage(''), 5000);
      }
    }, 100); // Small delay to ensure state is ready
  };


  const goTo = (section) => {
    setActiveSection(section);
    setSidebarOpen(false);
  };

  const toBase64 = (fileOrBlob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(fileOrBlob);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });



  const handleFaultPointClick = (index, type) => {
    setEditFaultIndex(index);
    setEditFaultType(type);
    setMessage(`Editing ${type} fault #${index + 1}`);
  };

  const handleFaultPointDelete = (index, type) => {
    handleDeleteFault(index, type);
    setEditFaultIndex(null);
    setEditFaultType(null);
  };

  // NEW: Handle fault coordinate updates from drag-and-drop
  const handleFaultUpdate = (index, newCoords, type) => {
    if (type === 'exterior') {
      setExteriorFaults(prev => prev.map((fault, i) =>
        i === index ? { ...fault, coords: newCoords } : fault
      ));
    } else if (type === 'interior') {
      setInteriorFaults(prev => prev.map((fault, i) =>
        i === index ? { ...fault, coords: newCoords } : fault
      ));
    }
  };


  const compressImage = async (base64, maxWidth = 800, maxHeight = 600, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      try {
        const img = new Image();
        img.src = base64;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = () => reject(new Error('Image compression failed'));
      } catch (error) {
        reject(error);
      }
    });
  };

  const startNewAppraisal = () => {
    // Clear all localStorage first
    if (user) {
      localStorage.removeItem(`currentAppraisalForm_${user.username}`);
      localStorage.removeItem(`currentExteriorFaults_${user.username}`);
      localStorage.removeItem(`currentInteriorFaults_${user.username}`);
      localStorage.removeItem(`currentAppraisalStep_${user.username}`);
      localStorage.removeItem(`appraisalInProgress_${user.username}`);
    }

    // Reset all state
    setForm(initialForm);
    setExteriorFaults([]);
    setInteriorFaults([]);
    setEditFaultIndex(null);
    setEditFaultType(null);
    setCurrentStep(1);
    setActiveSection('start');
    setIsEditMode(false);  // Reset edit mode
    setShowManualPopup(false);
    setShowRefreshWarning(false);
    setMessage('');
  };

  // Check if uploaded documents exist for a registration
  const hasUploadedDocuments = (reg) => {
    if (!reg) return false;
    const normalizedReg = reg.trim().toUpperCase();
    const uploadedDocs = JSON.parse(localStorage.getItem(`uploadedDocuments_${user?.username}`) || '[]');
    return uploadedDocs.some(doc => doc.registration?.trim().toUpperCase() === normalizedReg);
  };

  useEffect(() => {
    if (user) {
      const wasInProgress = localStorage.getItem(`appraisalInProgress_${user.username}`);

      if (wasInProgress && activeSection === 'dashboard') {
        // Page was refreshed during appraisal - clear everything and go to dashboard
        localStorage.removeItem(`currentAppraisalForm_${user.username}`);
        localStorage.removeItem(`currentExteriorFaults_${user.username}`);
        localStorage.removeItem(`currentInteriorFaults_${user.username}`);
        localStorage.removeItem(`currentAppraisalStep_${user.username}`);
        localStorage.removeItem(`appraisalInProgress_${user.username}`);
        return;
      }

      const savedForm = JSON.parse(localStorage.getItem(`currentAppraisalForm_${user.username}`) || '{}');
      if (Object.keys(savedForm).length > 0 && activeSection === 'start' && !isEditMode) {
        const loadedTyres = savedForm.tyres || [];
        const defaultTyres = [
          createEmptyTyre('Front Left'),
          createEmptyTyre('Front Right'),
          createEmptyTyre('Rear Left'),
          createEmptyTyre('Rear Right')
        ];
        const mergedTyres = defaultTyres.map((def, i) => ({ ...def, ...(loadedTyres[i] || {}) }));
        setForm({ ...savedForm, tyres: mergedTyres });
      }

      const savedExtFaults = JSON.parse(localStorage.getItem(`currentExteriorFaults_${user.username}`) || '[]');
      const savedIntFaults = JSON.parse(localStorage.getItem(`currentInteriorFaults_${user.username}`) || '[]');
      if (activeSection === 'start' && !isEditMode) {
        setExteriorFaults(savedExtFaults);
        setInteriorFaults(savedIntFaults);
      }

      const savedStep = localStorage.getItem(`currentAppraisalStep_${user.username}`);
      if (savedStep && activeSection === 'start' && !isEditMode) {
        setCurrentStep(parseInt(savedStep, 10));
      }
    }
  }, [user, activeSection]);

  useEffect(() => {
    const runAIGrade = async () => {
      try {
        const grade = await aiGradeFromFaults({
          exterior: exteriorFaults,
          interior: interiorFaults
        });
        setForm((prev) => ({ ...prev, overallGrade: grade }));
      } catch (error) {
        console.error('AI grading failed:', error);
      }
    };
    runAIGrade();
  }, [exteriorFaults, interiorFaults]);

  useEffect(() => {
    if (user) {
      // Exclude photo base64 from localStorage to prevent quota exceeded
      const formWithoutPhotos = { ...form };
      const exteriorFaultsWithoutPhotos = exteriorFaults.map(f => ({
        ...f,
        photo: f.photo?.startsWith('http') ? f.photo : null // Only keep Cloudinary URLs
      }));
      const interiorFaultsWithoutPhotos = interiorFaults.map(f => ({
        ...f,
        photo: f.photo?.startsWith('http') ? f.photo : null // Only keep Cloudinary URLs
      }));

      try {
        localStorage.setItem(`currentAppraisalForm_${user.username}`, JSON.stringify(formWithoutPhotos));
        localStorage.setItem(`currentExteriorFaults_${user.username}`, JSON.stringify(exteriorFaultsWithoutPhotos));
        localStorage.setItem(`currentInteriorFaults_${user.username}`, JSON.stringify(interiorFaultsWithoutPhotos));
        localStorage.setItem(`currentAppraisalStep_${user.username}`, currentStep.toString());
      } catch (e) {
        console.warn('localStorage save failed:', e.message);
      }
    }
  }, [form, exteriorFaults, interiorFaults, currentStep, user]);

  useEffect(() => {
    if (user?.id) {
      loadTasks();
    }
  }, [user]);

  useEffect(() => {
    setMessage('');
    setSidebarOpen(false);
  }, [activeSection]);

  // Auto-dismiss message after 3 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    let mounted = true;

    const clean = (str) => (str || '').trim();

    api.me()
      .then(u => {
        if (!mounted) return;
        if (!u || u.role !== 'staff') {
          navigate('/Home', { replace: true });
          return;
        }

        const parts = (u.name || '').split(/\s+/);
        const derivedFirst = parts[0] || '';
        const derivedLast = parts.slice(1).join(' ') || '';

        setUser({
          id: u.id,
          username: u.username,
          email: (u.email || '').trim(),
          firstName: clean(u.firstName) || derivedFirst || 'Staff',
          lastName: clean(u.lastName) || derivedLast,
          isTwoFactorEnabled: !!u.isTwoFactorEnabled // ✅ Fix: Pass 2FA status
        });

        return api.getAllAppraisals();
      })
      .then(allRes => {
        if (!mounted) return;

        const all = Array.isArray(allRes) ? allRes : (allRes?.data || []);
        const email = (user?.email || '').trim().toLowerCase();
        const username = (user?.username || '').trim().toLowerCase();
        const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim().toLowerCase();

        const matchesMe = (a) => {
          const e = ((a.submittedByEmail || a.createdByEmail || a.email) || '').trim().toLowerCase();
          const u = ((a.submittedByUsername || a.createdByUsername || a.username) || '').trim().toLowerCase();
          const n = ((a.submittedBy || a.createdBy || '')).trim().toLowerCase();
          return (email && e === email) || (username && u === username) || (fullName && n === fullName);
        };

        const mine = (all || []).filter(matchesMe);

        const local = JSON.parse(localStorage.getItem(`appraisals_${user?.username}`) || '[]');
        const byId = Object.fromEntries(
          local.filter(x => x && x._id).map(x => [x._id, x])
        );

        const merged = mine.map(a => {
          const l = byId[a._id];
          return l
            ? {
              ...a,
              aiSummary: a.aiSummary || l.aiSummary,
              overallGrade: a.overallGrade || l.overallGrade,
              vehicle: `${a.make || ''} ${a.model || ''}`.trim() || 'N/A'
            }
            : {
              ...a,
              vehicle: `${a.make || ''} ${a.model || ''}`.trim() || 'N/A'
            };
        });

        setAppraisals(merged);
        if (user?.username) {
          localStorage.setItem(`appraisals_${user.username}`, JSON.stringify(merged));
        }
      })
      .catch(err => console.error(err));

    return () => { mounted = false };
  }, [navigate, user?.username, user?.email, user?.firstName, user?.lastName]);


  // Build notifications when appraisals change
  useEffect(() => {
    if (appraisals.length > 0) {
      // Load viewed notifications from localStorage
      const viewed = JSON.parse(localStorage.getItem(`viewedNotifications_${user?.username}`) || '[]');
      setViewedNotifications(viewed);

      const notifs = appraisals
        .filter(a => a.approvalStatus && a.approvalStatus !== 'pending' && a.adminMessage)
        .map(a => ({
          id: a._id,
          reg: a.reg,
          status: a.approvalStatus,
          message: a.adminMessage,
          date: a.reviewedAt || a.date
        }))
        .sort((a, b) => {
          // Sort by date - newest first
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB - dateA;
        });
      setNotifications(notifs);
    } else {
      setNotifications([]);
    }
  }, [appraisals, user?.username]);

  // ✅ NEW: Fetch all uploaded documents once at parent level
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const docs = await api.getUploadedDocuments();
        setUploadedDocuments(docs || []);
      } catch (error) {
        console.error('Error fetching documents:', error);
        setUploadedDocuments([]);
      }
    };

    // Only fetch if user is logged in
    if (user?.username) {
      fetchDocuments();
    }
  }, [user?.username, activeSection]); // Re-fetch when section changes

  // ✅ OPTIMIZED: Folder Button Component now receives hasDocuments as prop
  const FolderButton = ({ regNumber, hasDocuments, onClick, onAdd }) => {
    const handleClick = () => {
      if (hasDocuments) {
        // Has documents - open folder
        onClick(regNumber);
      } else {
        // No documents - go to upload
        onAdd(regNumber);
      }
    };

    return (
      <button
        className="folder-btn"
        onClick={handleClick}
        title={hasDocuments ? `View documents for ${regNumber}` : `Upload documents for ${regNumber}`}
      >
        {hasDocuments ? '📁' : '➕'}
      </button>
    );
  };

  // Folder Click Handler
  const handleFolderClick = (regNumber) => {
    setSelectedRegNumber(regNumber);
    setActiveSection('upload');
  };

  // Add Documents Handler (for + button)
  const handleAddDocuments = (regNumber) => {
    setSelectedRegNumber(regNumber);
    setActiveSection('upload');
    // Note: UploadDocumentsSection will handle pre-filling the reg number
  };



  // Handle beforeunload warning ONLY when in appraisal (step >= 1)
  useEffect(() => {
    if (activeSection === 'start' && currentStep >= 1) {
      if (user) {
        localStorage.setItem(`appraisalInProgress_${user.username}`, 'true');
      }

      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = 'Are you sure you want to refresh the page? All unsaved progress will be lost.';
        return 'Are you sure you want to refresh the page? All unsaved progress will be lost.';
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    } else {
      if (user) {
        localStorage.removeItem(`appraisalInProgress_${user.username}`);
      }
    }
  }, [activeSection, currentStep, user]);


  // Prevent logout on browser back button
  useEffect(() => {
    const handlePopState = (e) => {
      // Push current state back to prevent default back navigation
      window.history.pushState(null, '', window.location.pathname);
    };

    // Add initial state
    window.history.pushState(null, '', window.location.pathname);

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleLogout = async () => {
    if (!window.confirm("Sign out?")) return;
    try {
      await api.logout();
    } catch (err) {
      console.error(err);
    }
    navigate('/Home', { replace: true });
  };

  const nextStep = () => {
    if (currentStep < 7) setCurrentStep(currentStep + 1);
  };
  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleFindVehicle = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();

    const regTrim = (form.reg || '').trim().toUpperCase();
    if (!regTrim) {
      setMessage('Please enter a registration number.');
      return;
    }

    const ukRegRegex = /^[A-Z0-9]{2,4}\s?[A-Z0-9]{2,3}$/i;

    if (!ukRegRegex.test(regTrim)) {
      setForm(prev => ({ ...prev, reg: regTrim, vin: '' }));
      setShowManualPopup(true);
      setCurrentStep(2);
      return;
    }

    setLoadingVehicle(true);
    setMessage('');

    try {
      const data = await api.lookupVehicle(regTrim);

      if (!data || !data.make) {
        setForm(prev => ({ ...prev, reg: regTrim, vin: '' }));
        setShowManualPopup(true);
        setCurrentStep(2);
        return;
      }

      const S = (v) => (v == null ? '' : String(v));
      const derivedYear =
        data.yearOfManufacture
          ? S(data.yearOfManufacture)
          : data.monthOfFirstRegistration
            ? S(new Date(data.monthOfFirstRegistration).getFullYear())
            : '';

      setForm((prev) => ({
        ...prev,
        reg: regTrim,
        make: S(data.make) || prev.make,
        model: S(data.model) || prev.model,
        colour: S(data.colour) || S(data.color) || prev.colour,
        fuelType: S(data.fuelType) || prev.fuelType,
        vin: S(data.vin || data.vehicleIdentificationNumber) || prev.vin,
        engineSize: S(data.engineCapacity) || prev.engineSize,
        co2: S(data.co2Emissions) || prev.co2,
        euroStatus: S(data.euroStatus) || prev.euroStatus,
        wheelplan: S(data.wheelplan) || prev.wheelplan,
        regDate: S(data.monthOfFirstRegistration) || prev.regDate,
        year: derivedYear || prev.year,
        yearOfManufacture: S(data.yearOfManufacture) || prev.yearOfManufacture,
        dateOfLastV5CIssued: S(data.dateOfLastV5CIssued) || prev.dateOfLastV5CIssued,
        motStatus: S(data.motStatus) || prev.motStatus,
        taxStatus: S(data.taxStatus) || prev.taxStatus,
        taxDueDate: S(data.taxDueDate) || prev.taxDueDate,
        revenueWeight: S(data.revenueWeight) || prev.revenueWeight,
        transmission: prev.transmission,
        mileage: prev.mileage,
        artEndDate: prev.artEndDate,
      }));

      setMessage('✅ Vehicle details fetched successfully!');
      nextStep();
    } catch (err) {
      console.error(err);
      setForm(prev => ({ ...prev, vin: '', reg: regTrim }));
      setShowManualPopup(true);
      setCurrentStep(2);
    } finally {
      setLoadingVehicle(false);
    }
  };

  const recomputeGlobalIdx = (ext, int) => {
    const ext2 = ext.map((f, i) => ({ ...f, idx: i + 1 }));
    const int2 = int.map((f, i) => ({ ...f, idx: ext2.length + i + 1 }));
    return { ext2, int2 };
  };

  const handleAddFault = async (fault, type) => {
    try {
      if (fault.photo) {
        if (fault.photo instanceof File) {
          const b64 = await compressImage(await toBase64(fault.photo));
          const url = await api.uploadFaultPhoto(b64);
          fault.photoUrl = url;
          delete fault.photo;
        } else if (typeof fault.photo === 'string' && fault.photo.startsWith('data:image/')) {
          const url = await api.uploadFaultPhoto(fault.photo);
          fault.photoUrl = url;
          delete fault.photo;
        }
      }

      const totalFaults = exteriorFaults.length + interiorFaults.length;
      const faultWithIdx = { ...fault, idx: totalFaults + 1 };

      if (type === 'exterior') {
        if (editFaultIndex !== null && editFaultType === 'exterior') {
          const updated = [...exteriorFaults];
          updated[editFaultIndex] = faultWithIdx;
          setExteriorFaults(updated);
          setEditFaultIndex(null);
          setEditFaultType(null);
        } else {
          setExteriorFaults(prev => [...prev, faultWithIdx]);
        }
      } else if (type === 'interior') {
        if (editFaultIndex !== null && editFaultType === 'interior') {
          const updated = [...interiorFaults];
          updated[editFaultIndex] = faultWithIdx;
          setInteriorFaults(updated);
          setEditFaultIndex(null);
          setEditFaultType(null);
        } else {
          setInteriorFaults(prev => [...prev, faultWithIdx]);
        }
      }
      setMessage(`Fault added: ${fault.part} - ${fault.damage}`);
    } catch (error) {
      console.error('Error adding fault:', error);
      setMessage('❌ Failed to add fault. Please try again.');
    }
  };

  const handleDeleteFault = (index, type) => {
    if (type === 'exterior') {
      const nextExt = exteriorFaults.filter((_, i) => i !== index);
      const nextInt = [...interiorFaults];
      const { ext2, int2 } = recomputeGlobalIdx(nextExt, nextInt);
      setExteriorFaults(ext2);
      setInteriorFaults(int2);
    } else if (type === 'interior') {
      const nextExt = [...exteriorFaults];
      const nextInt = interiorFaults.filter((_, i) => i !== index);
      const { ext2, int2 } = recomputeGlobalIdx(nextExt, nextInt);
      setExteriorFaults(ext2);
      setInteriorFaults(int2);
    }
    setMessage('Fault deleted.');
  };

  const handleEditFault = (index, type) => {
    setEditFaultIndex(index);
    setEditFaultType(type);
    setMessage(`Editing ${type} fault at index ${index}`);
  };

  const handleFinalSubmit = async () => {
    if (submitLoading) return;
    setSubmitLoading(true);

    const newAppraisal = {
      ...form,
      reg: (form.reg || '').toUpperCase(),
      vehicle: `${form.make} ${form.model}`.trim(),
      date: new Date().toLocaleDateString(),
      submittedBy: `${user.firstName} ${user.lastName}`,
      submittedByEmail: (user.email || '').trim(),
      submittedByUsername: (user.username || '').trim(),
      faults: { exterior: exteriorFaults, interior: interiorFaults },
    };

    // DEBUG: Log to verify fromPrevious flags are being saved
    console.log('🔍 Appraisal Faults Debug:', {
      exteriorCount: exteriorFaults.length,
      interiorCount: interiorFaults.length,
      exteriorSample: exteriorFaults[0],
      interiorSample: interiorFaults[0],
      exteriorFromPrevious: exteriorFaults.filter(f => f.fromPrevious).length,
      interiorFromPrevious: interiorFaults.filter(f => f.fromPrevious).length
    });

    // YE LINE ADD KAREIN - Debug ke liye
    console.log('📤 Submitting Appraisal with Step 6 data:', {
      deviceType: newAppraisal.deviceType, // Debug device type
      vehicleMileage: newAppraisal.vehicleMileage,
      mileageModifications: newAppraisal.mileageModifications,
      hasV5Document: newAppraisal.hasV5Document,
      numberOfOwners: newAppraisal.numberOfOwners,
      numberOfKeys: newAppraisal.numberOfKeys,
      firstRegisteredUK: newAppraisal.firstRegisteredUK,
      insuranceWriteOff: newAppraisal.insuranceWriteOff,
      serviceHistoryType: newAppraisal.serviceHistoryType,
      motStatus: newAppraisal.motStatus
    });
    try {
      try {
        if (!form.overallGrade) {
          const aiGrade = await aiGradeFromFaults({
            exterior: exteriorFaults,
            interior: interiorFaults,
          });
          newAppraisal.overallGrade = aiGrade;
        }

        const summary = await aiSummarizeAppraisal({
          ...newAppraisal,
          overallGrade: newAppraisal.overallGrade || form.overallGrade,
        });

        newAppraisal.aiSummary = summary;
      } catch (err) {
        console.error("AI summary failed:", err);
        newAppraisal.aiSummary = "AI summary unavailable.";
      }

      // ✅ Generate PDF for email attachment
      let pdfBase64 = null;
      try {
        console.log('📄 Generating PDF for email attachment...');
        const pdfBlob = await generatePDFUtil(newAppraisal, { mode: 'blob' });

        // Convert blob to base64
        const reader = new FileReader();
        pdfBase64 = await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(pdfBlob);
        });

        console.log('✅ PDF generated successfully for email');
      } catch (pdfErr) {
        console.error('Failed to generate PDF for email:', pdfErr);
        // Continue without PDF attachment
      }

      // Add PDF to appraisal data if generated
      if (pdfBase64) {
        newAppraisal.pdfBase64 = pdfBase64;
      }

      const isUpdate = !!form._id;
      const saved = isUpdate
        ? await api.updateAppraisal(form._id, newAppraisal)
        : await api.createAppraisal(newAppraisal);

      const savedWithAI = {
        ...(saved || {}),
        aiSummary: newAppraisal.aiSummary,
        overallGrade: newAppraisal.overallGrade || form.overallGrade,
      };

      if (!isUpdate) {
        setAppraisals(prev => [savedWithAI || newAppraisal, ...prev]);
      } else {
        setAppraisals(prev =>
          prev.map(a => (a._id === form._id ? (savedWithAI || newAppraisal) : a))
        );
      }

      setActiveSection('submitted');

      try {
        const allRes = await api.getAllAppraisals();
        const all = Array.isArray(allRes) ? allRes : (allRes?.data || []);

        const email = (user.email || '').trim().toLowerCase();
        const username = (user.username || '').trim().toLowerCase();
        const fullName = [user.firstName, user.lastName]
          .filter(Boolean).join(' ').trim().toLowerCase();

        const matchesMe = (a) => {
          const e = ((a.submittedByEmail || a.createdByEmail || a.email) || '').trim().toLowerCase();
          const u = ((a.submittedByUsername || a.createdByUsername || a.username) || '').trim().toLowerCase();
          const n = ((a.submittedBy || a.createdBy || '')).trim().toLowerCase();
          return (email && e === email) || (username && u === username) || (fullName && n === fullName);
        };

        const mine = (all || []).filter(matchesMe);

        const minePatched = mine.map(a => {
          if (saved && a._id === saved._id) {
            return {
              ...a,
              aiSummary: a.aiSummary || newAppraisal.aiSummary,
              overallGrade: a.overallGrade || newAppraisal.overallGrade,
            };
          }
          return a;
        });

        setAppraisals(minePatched);
        localStorage.setItem(`appraisals_${user.username}`, JSON.stringify(minePatched));
      } catch (refreshErr) {
        console.warn("Refresh after submit failed, keeping optimistic item.", refreshErr);
      }

      localStorage.removeItem(`currentAppraisalForm_${user.username}`);
      localStorage.removeItem(`currentExteriorFaults_${user.username}`);
      localStorage.removeItem(`currentInteriorFaults_${user.username}`);
      localStorage.removeItem(`currentAppraisalStep_${user.username}`);

      setForm(initialForm);
      setExteriorFaults([]);
      setInteriorFaults([]);
      setCurrentStep(1);

      // If this was a linked task, mark it as Completed
      if (newAppraisal.linkedTaskId) {
        try {
          await api.updateTask(newAppraisal.linkedTaskId, { status: 'Completed' });
          // Update local tasks state
          setTasks(prev => prev.map(t => t._id === newAppraisal.linkedTaskId ? { ...t, status: 'Completed' } : t));
          console.log('✅ Linked task marked as Completed');
        } catch (taskErr) {
          console.error('Failed to complete linked task:', taskErr);
        }
      }

      setMessage(isUpdate ? '✅ Appraisal updated with AI summary!' : '✅ Appraisal submitted with AI summary!');
    } catch (e) {
      console.error('saveAppraisal failed:', e);
      setMessage('❌ Server save failed. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const generatePDF = async (data, { mode = 'download' } = {}) => {
    setPdfLoading(true);
    try {
      const pdfUrl = await generatePDFUtil(data, { mode });
      if (mode === 'preview' && pdfUrl) {
        setPdfPreviewUrl(pdfUrl);
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      setMessage("❌ Failed to generate PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const deleteAppraisal = async (i) => {
    const appraisal = appraisals[i];
    if (!appraisal || !appraisal._id) {
      setMessage('❌ Cannot delete: missing ID.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete appraisal ${appraisal.reg}?`)) return;

    try {
      await api.deleteAppraisal(appraisal._id);

      const updated = appraisals.filter((_, idx) => idx !== i);
      setAppraisals(updated);
      localStorage.setItem(`appraisals_${user.username}`, JSON.stringify(updated));

      setMessage('✅ Appraisal deleted from server!');
    } catch (err) {
      console.error('Delete failed:', err);
      setMessage('❌ Failed to delete appraisal from server.');
    }
  };

  const handleSearch = () => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      setMessage('Please enter a search keyword.');
      setSearchResults([]);
      return;
    }
    const results = appraisals.filter(a =>
      (a.reg || '').toLowerCase().includes(query) ||
      ((a.make || '') + ' ' + (a.model || '')).toLowerCase().includes(query) ||
      (a.ownerName || '').toLowerCase().includes(query)
    );
    setSearchResults(results);
    setMessage(`Found ${results.length} results.`);
  };

  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(''), 3000);
    return () => clearTimeout(timer);
  }, [message]);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-blue-600 mx-auto mb-4" />
          <p className="text-gray-900">Loading...</p>
        </div>
      </div>
    );
  }

  const NavBtn = ({ icon, label, isActive = false, onClick }) => (
    <button
      onClick={onClick}
      aria-pressed={isActive}
      className={`flex items-center gap-3 w-full text-left px-4 py-2 rounded-md transition ${isActive ? "bg-blue-50 text-blue-700" : "text-gray-900 hover:bg-gray-50 hover:text-blue-700"
        }`}
    >
      {icon}
      <span className={`inline ${desktopCollapsed ? "lg:hidden" : "lg:inline"}`}>{label}</span>
    </button>
  );

  return (
    <div className="h-[100dvh] bg-gray-50 flex overflow-hidden">
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <div
        className={`fixed inset-y-0 left-0 z-50 bg-white border-r shadow-sm
          transform transition-transform duration-200
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          flex flex-col justify-between overflow-y-auto
          w-72 ${desktopCollapsed ? "lg:w-20" : "lg:w-72"}`}
      >
        <div>
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2">
              <FaHome className="text-blue-700" size={20} />
              <h2 className={`text-lg font-bold text-gray-800 block ${desktopCollapsed ? "lg:hidden" : "lg:block"}`}>
                Staff Panel
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="hidden lg:inline-flex p-2 rounded-md hover:bg-gray-100"
                onClick={() => setDesktopCollapsed((v) => !v)}
                title={desktopCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {desktopCollapsed ? (
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
              <button className="lg:hidden p-2 rounded-md hover:bg-gray-100" onClick={() => setSidebarOpen(false)} aria-label="Close sidebar">
                <FaTimes className="text-gray-900" size={20} />
              </button>
            </div>
          </div>

          <nav className="p-3 space-y-2">
            <NavBtn icon={<FaHome size={18} />} label="Dashboard" isActive={activeSection === "dashboard"} onClick={() => goTo("dashboard")} />
            <NavBtn icon={<FaPlusCircle size={18} />} label="Start New Appraisal" isActive={activeSection === "start"} onClick={() => goTo("start")} />
            <NavBtn icon={<FaClipboardList size={18} />} label="My Appraisals" isActive={activeSection === "submitted"} onClick={() => goTo("submitted")} />
            <button
              onClick={() => goTo("tasks")}
              aria-pressed={activeSection === "tasks"}
              className={`flex items-center gap-3 w-full text-left px-4 py-2 rounded-md transition ${activeSection === "tasks" ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:bg-gray-50 hover:text-blue-700"
                }`}
            >
              <FaTasks size={18} />
              <span className={`inline ${desktopCollapsed ? "lg:hidden" : "lg:inline"}`}>My Jobs</span>
              {tasks.filter(t => t.status === 'Pending').length > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {tasks.filter(t => t.status === 'Pending').length}
                </span>
              )}
            </button>
            <NavBtn icon={<FaSearch size={18} />} label="Search" isActive={activeSection === "search"} onClick={() => goTo("search")} />
            <NavBtn icon={<FaUpload size={18} />} label="Upload Documents" isActive={activeSection === "upload"} onClick={() => goTo("upload")} />
          </nav>
        </div>

        <div className="p-4 border-t">
          <button onClick={handleLogout} className="flex items-center gap-2 text-red-600 font-semibold hover:text-red-700 w-full justify-start">
            <FaSignOutAlt size={18} />
            <span className={`inline ${desktopCollapsed ? "lg:hidden" : "lg:inline"}`}>Logout</span>
          </button>
        </div>
      </div>

      <div className={`flex-1 p-4 lg:p-8 overflow-y-auto h-[100dvh] [-webkit-overflow-scrolling:touch] overscroll-y-contain ${desktopCollapsed ? "lg:ml-20" : "lg:ml-72"}`}>
        <div className="flex items-center justify-between mb-8 lg:mb-10">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border bg-white hover:bg-gray-50"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar"
            >
              <FaBars className="text-gray-900" size={20} />
            </button>
            {activeSection === "dashboard" ? (
              <img
                src={businessLogo}
                alt="Heston Workspace"
                className="h-8 lg:h-10 w-auto object-contain"
              />
            ) : (
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 capitalize">
                {activeSection === 'start'
                  ? 'New Appraisal'
                  : activeSection === 'submitted'
                    ? 'My Appraisals'
                    : activeSection}
              </h1>
            )}
          </div>

          {/* RIGHT SIDE: Notifications + User menu */}
          <div className="flex items-center gap-3">
            {/* NEW APPRAISAL BUTTON */}

            {/* Notification Bell */}
            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 rounded-full hover:bg-gray-100"
                aria-label="Notifications"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {(() => {
                  const unviewedCount = notifications.filter(n => !viewedNotifications.includes(n.id)).length;
                  const unviewedTasksCount = tasks.filter(t => t.status === 'Pending' && !viewedTaskNotifications.includes(t._id)).length;
                  const totalCount = unviewedCount + unviewedTasksCount;
                  return totalCount > 0 && (
                    <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {totalCount}
                    </span>
                  );
                })()}
              </button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                  <div className="p-3 border-b font-semibold">
                    Notifications ({notifications.length + tasks.filter(t => t.status === 'Pending').length})
                  </div>

                  {/* Pending Jobs Section */}
                  {tasks.filter(t => t.status === 'Pending').length > 0 && (
                    <>
                      <div className="p-2 bg-gray-50 text-xs font-semibold text-gray-600">
                        Pending Jobs
                      </div>
                      {tasks.filter(t => t.status === 'Pending').map(task => {
                        const isViewed = viewedTaskNotifications.includes(task._id);
                        return (
                          <div
                            key={task._id}
                            className={`p-3 border-b cursor-pointer transition-colors ${!isViewed
                              ? 'bg-yellow-50 hover:bg-yellow-100 border-l-4 border-l-yellow-500'
                              : 'hover:bg-gray-50'
                              }`}
                            onClick={() => {
                              // Mark task as viewed
                              if (!isViewed) {
                                const newViewed = [...viewedTaskNotifications, task._id];
                                setViewedTaskNotifications(newViewed);
                                localStorage.setItem(`viewedTaskNotifications_${user?.username}`, JSON.stringify(newViewed));
                              }
                              setShowNotifications(false);
                              goTo('tasks');
                            }}
                          >
                            <div className="flex items-start gap-2">
                              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${!isViewed ? 'bg-yellow-500' : 'bg-gray-400'}`} />
                              <div className="flex-1">
                                <div className="font-semibold text-sm">
                                  📋 New Job: {task.title}
                                </div>
                                {task.description && (
                                  <div className="text-sm text-gray-600 mt-1">
                                    {task.description}
                                  </div>
                                )}
                                <div className="text-xs text-gray-400 mt-1 flex items-center gap-2">
                                  <span className={`px-2 py-0.5 rounded-full ${task.priority === 'High' ? 'bg-red-100 text-red-800' :
                                    task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-green-100 text-green-800'
                                    }`}>
                                    {task.priority}
                                  </span>
                                  {task.deadline && <span>Due: {new Date(task.deadline).toLocaleDateString()}</span>}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}

                  {/* Appraisal Notifications Section */}
                  {notifications.length > 0 && (
                    <>
                      <div className="p-2 bg-gray-50 text-xs font-semibold text-gray-600">
                        Appraisal Updates
                      </div>
                      {notifications.map(notif => {
                        const isViewed = viewedNotifications.includes(notif.id);
                        return (
                          <div
                            key={notif.id}
                            className={`p-3 border-b cursor-pointer transition-colors ${!isViewed
                              ? 'bg-blue-100 hover:bg-blue-200 border-l-4 border-l-blue-500'
                              : 'hover:bg-gray-50'
                              }`}
                            onClick={() => {
                              // Mark as viewed
                              if (!isViewed) {
                                const newViewed = [...viewedNotifications, notif.id];
                                setViewedNotifications(newViewed);
                                localStorage.setItem(`viewedNotifications_${user?.username}`, JSON.stringify(newViewed));
                              }

                              // Close dropdown and go to appraisals
                              setShowNotifications(false);
                              goTo('submitted');
                            }}
                          >
                            <div className="flex items-start gap-2">
                              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${notif.status === 'approved' ? 'bg-green-500' : 'bg-red-500'
                                }`} />
                              <div className="flex-1">
                                <div className="font-semibold text-sm">
                                  {notif.reg} - {notif.status === 'approved' ? '✓ Approved' : '✗ Rejected'}
                                </div>
                                {notif.message && (
                                  <div className="text-sm text-gray-600 mt-1">
                                    {notif.message}
                                  </div>
                                )}
                                <div className="text-xs text-gray-400 mt-1">
                                  {new Date(notif.date).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}

                  {notifications.length === 0 && tasks.filter(t => t.status === 'Pending').length === 0 && (
                    <div className="p-6 text-center text-gray-500">
                      No notifications
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* User Menu (existing) */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(v => !v)}
                className="flex items-center gap-2 bg-white border rounded-full pl-1 pr-3 py-1.5 shadow-sm hover:bg-gray-50"
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
              >
                <div className="w-8 h-8 rounded-full bg-blue-600/10 text-blue-700 font-semibold grid place-items-center">
                  {initials}
                </div>
                <span className="hidden sm:inline text-gray-800 font-medium">
                  {displayName}
                </span>
                <ChevronDown size={16} className="text-gray-900" />
              </button>

              {userMenuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-50"
                >
                  <button
                    onClick={() => {
                      setActiveSection('settings');
                      setUserMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 text-gray-700"
                  >
                    <FaTasks size={16} /> {/* Re-using FaTasks or import FaCog if needed, let's just use text for now or existing icon */}
                    <span>Settings</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 text-red-600"
                  >
                    <FaSignOutAlt size={16} />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>


        {activeSection === "settings" && (
          <div className="max-w-4xl mx-auto p-4 md:p-6 lg:p-8">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-xl font-bold mb-4">Account Settings</h2>
              <div className="p-4 bg-blue-50 text-blue-800 rounded-lg flex items-center gap-3">
                <div className="text-2xl">🛡️</div>
                <div>
                  <h3 className="font-bold">Two-Factor Authentication</h3>
                  <p className="text-sm mt-1">
                    2FA is currently <span className={`font-bold ${user.isTwoFactorEnabled ? 'text-green-600' : 'text-gray-600'}`}>{user.isTwoFactorEnabled ? 'ENABLED' : 'DISABLED'}</span>.
                  </p>
                  <p className="text-xs text-blue-600 mt-1">This setting is managed by your Administrator.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === "dashboard" &&
          (() => {
            // ===== Derived data for dashboard =====
            const totalAppraisals = appraisals.length;

            // Last 5
            const lastFive = appraisals.slice(0, 5);

            // Weekly count (last 7 days)
            const weeklyCount = appraisals.filter((a) => {
              if (!a.date) return false;
              const d = new Date(a.date);
              if (isNaN(d.getTime())) return false;
              const now = new Date();
              const diffDays =
                (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24);
              return diffDays <= 7;
            }).length;

            // Grades
            // Grades as NUMBERS (1–5). U ya blank ignore.
            const numericGrades = appraisals
              .map((a) => Number(String(a.overallGrade ?? "").trim()))
              .filter((n) => !Number.isNaN(n) && n >= 1 && n <= 5);

            const gradeCounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
            numericGrades.forEach((n) => {
              gradeCounts[n] = (gradeCounts[n] || 0) + 1;
            });

            const hasAnyGrades = numericGrades.length > 0;

            // Average numeric grade (e.g. 2.4)
            let avgGrade = "N/A";
            if (numericGrades.length > 0) {
              const sum = numericGrades.reduce((acc, n) => acc + n, 0);
              avgGrade = (sum / numericGrades.length).toFixed(1);
            }

            // Donut chart degrees (1–5)
            const totalForPie = numericGrades.length || 1;
            const d1 = (gradeCounts[1] / totalForPie) * 360;
            const d2 = (gradeCounts[2] / totalForPie) * 360;
            const d3 = (gradeCounts[3] / totalForPie) * 360;
            const d4 = (gradeCounts[4] / totalForPie) * 360;
            const d5 = (gradeCounts[5] / totalForPie) * 360;

            // Monthly data (last 6 months)
            const monthCounts = {};
            const monthNames = [
              "Jan", "Feb", "Mar", "Apr", "May", "Jun",
              "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
            ];

            appraisals.forEach((a) => {
              if (!a.date) return;
              const d = new Date(a.date);
              if (isNaN(d.getTime())) return;
              const key = `${d.getFullYear()}-${d.getMonth()}`;
              monthCounts[key] = (monthCounts[key] || 0) + 1;
            });

            const last6Months = [];
            const now = new Date();
            for (let i = 5; i >= 0; i--) {
              const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
              const key = `${d.getFullYear()}-${d.getMonth()}`;
              last6Months.push({
                label: monthNames[d.getMonth()],
                count: monthCounts[key] || 0,
              });
            }
            const maxMonthCount =
              Math.max(...last6Months.map((m) => m.count), 1);

            // Fault analysis
            let totalExt = 0;
            let totalInt = 0;
            appraisals.forEach((a) => {
              totalExt += (a.faults?.exterior || []).length;
              totalInt += (a.faults?.interior || []).length;
            });
            const totalFaults = totalExt + totalInt || 1;
            const extPercent = (totalExt / totalFaults) * 100;
            const intPercent = (totalInt / totalFaults) * 100;

            // Get last appraisal for "Continue" button
            const lastAppraisal = appraisals[0];

            // ===== JSX =====
            return (
              <>
                {/* GREETING + WEATHER + STATS ROW */}
                <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
                  <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900">
                        {getGreetingLabel()}, {displayName}!
                      </h2>
                      <p className="text-gray-600 mt-1">
                        Ready to create outstanding appraisals today?
                      </p>
                    </div>

                    {!weather.loading && (
                      <div className="flex flex-col items-center gap-2 bg-gradient-to-r from-blue-400 to-cyan-400 text-white px-6 py-4 rounded-xl shadow-md min-w-[150px]">
                        <div className="text-xs font-semibold uppercase tracking-wide">
                          {weather.location}
                        </div>
                        <div className="flex items-center gap-3">
                          {weather.lottieAnim && (
                            <div className="w-12 h-12">
                              <Lottie animationData={weather.lottieAnim} loop={true} />
                            </div>
                          )}
                          <div className="text-3xl font-bold">{weather.tempC}°C</div>
                        </div>
                        <div className="text-xs capitalize">{weather.desc}</div>
                      </div>
                    )}
                  </div>

                  {/* COMPACT STATS ROW */}
                  <div className="grid grid-cols-3 gap-4">
                    <div
                      className="text-center p-4 bg-blue-50 rounded-lg border border-blue-100 cursor-pointer hover:shadow-md transition"
                      onClick={() => goTo("submitted")}
                    >
                      <div className="text-3xl font-bold text-blue-600">{totalAppraisals}</div>
                      <div className="text-sm font-medium text-gray-600 mt-1">Total Appraisals</div>
                    </div>

                    <div
                      className="text-center p-4 bg-green-50 rounded-lg border border-green-100 cursor-pointer hover:shadow-md transition"
                      onClick={() => {
                        setFilterStatus('approved');
                        setTimeout(() => {
                          document.getElementById('approval-tables-section')?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }}
                    >
                      <div className="text-3xl font-bold text-green-600">
                        {appraisals.filter(a => a.approvalStatus === 'approved').length}
                      </div>
                      <div className="text-sm font-medium text-gray-600 mt-1">Approved</div>
                    </div>

                    <div
                      className="text-center p-4 bg-red-50 rounded-lg border border-red-100 cursor-pointer hover:shadow-md transition"
                      onClick={() => {
                        setFilterStatus('rejected');
                        setTimeout(() => {
                          document.getElementById('approval-tables-section')?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }}
                    >
                      <div className="text-3xl font-bold text-red-600">
                        {appraisals.filter(a => a.approvalStatus === 'rejected').length}
                      </div>
                      <div className="text-sm font-medium text-gray-600 mt-1">Rejected</div>
                    </div>
                  </div>
                </div>

                {/* TWO-COLUMN LAYOUT */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">

                  {/* LEFT COLUMN - ACTIVE WORK (2/3 width) */}
                  <div className="lg:col-span-2 space-y-6">

                    {/* QUICK ACTIONS */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <button
                          onClick={() => {
                            startNewAppraisal();
                            goTo("start");
                          }}
                          className="flex flex-col items-center gap-3 p-4 rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition cursor-pointer"
                        >
                          <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                            <FaPlusCircle size={24} />
                          </div>
                          <span className="text-sm font-semibold text-gray-900">New Appraisal</span>
                        </button>

                        {lastAppraisal && (
                          <button
                            onClick={() => {
                              setForm(lastAppraisal);
                              setExteriorFaults(lastAppraisal.faults?.exterior || []);
                              setInteriorFaults(lastAppraisal.faults?.interior || []);
                              setCurrentStep(2);
                              setActiveSection('start');
                            }}
                            className="flex flex-col items-center gap-3 p-4 rounded-xl border-2 border-gray-200 hover:border-green-500 hover:bg-green-50 transition cursor-pointer"
                          >
                            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-green-100 text-green-600">
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                            <span className="text-sm font-semibold text-gray-900">Continue Last</span>
                          </button>
                        )}

                        <button
                          onClick={() => goTo("submitted")}
                          className="flex flex-col items-center gap-3 p-4 rounded-xl border-2 border-gray-200 hover:border-purple-500 hover:bg-purple-50 transition cursor-pointer"
                        >
                          <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-purple-100 text-purple-600">
                            <FaClipboardList size={24} />
                          </div>
                          <span className="text-sm font-semibold text-gray-900">My Appraisals</span>
                        </button>

                        <button
                          onClick={() => goTo("search")}
                          className="flex flex-col items-center gap-3 p-4 rounded-xl border-2 border-gray-200 hover:border-orange-500 hover:bg-orange-50 transition cursor-pointer"
                        >
                          <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-orange-100 text-orange-600">
                            <FaSearch size={24} />
                          </div>
                          <span className="text-sm font-semibold text-gray-900">Search</span>
                        </button>

                        <button
                          onClick={() => goTo("upload")}
                          className="flex flex-col items-center gap-3 p-4 rounded-xl border-2 border-gray-200 hover:border-yellow-500 hover:bg-yellow-50 transition cursor-pointer"
                        >
                          <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-yellow-100 text-yellow-600">
                            <FaUpload size={24} />
                          </div>
                          <span className="text-sm font-semibold text-gray-900">Upload</span>
                        </button>

                        <button
                          onClick={() => goTo("tasks")}
                          className="flex flex-col items-center gap-3 p-4 rounded-xl border-2 border-gray-200 hover:border-indigo-500 hover:bg-indigo-50 transition cursor-pointer relative"
                        >
                          <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
                            <FaTasks size={24} />
                          </div>
                          <span className="text-sm font-semibold text-gray-900">My Jobs</span>
                          {tasks.filter(t => t.status === 'Pending').length > 0 && (
                            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                              {tasks.filter(t => t.status === 'Pending').length}
                            </span>
                          )}
                        </button>
                      </div>
                    </div>


                    {/* MY JOBS WIDGET */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h2 className="text-xl font-bold text-gray-900">My Jobs</h2>
                          <p className="text-sm text-gray-500 mt-1">Jobs assigned by admin</p>
                        </div>
                        <button
                          onClick={() => goTo("tasks")}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          View All →
                        </button>
                      </div>

                      {tasksLoading ? (
                        <div className="text-center py-8">
                          <FaSpinner className="animate-spin text-blue-600 text-2xl mx-auto mb-2" />
                          <p className="text-gray-500">Loading jobs...</p>
                        </div>
                      ) : tasks.length === 0 ? (
                        <div className="text-center py-12">
                          <div className="text-6xl mb-4">✅</div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">No jobs assigned</h3>
                          <p className="text-gray-500">You're all caught up!</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {tasks.slice(0, 3).map((task) => {
                            const isNew = !viewedTaskNotifications.includes(task._id);
                            return (
                              <div
                                key={task._id}
                                className={`p-4 rounded-lg border transition-all cursor-pointer hover:border-blue-300 hover:bg-blue-50 ${isNew ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200'
                                  }`}
                                onClick={() => {
                                  if (isNew) {
                                    const newViewed = [...viewedTaskNotifications, task._id];
                                    setViewedTaskNotifications(newViewed);
                                    localStorage.setItem(`viewedTaskNotifications_${user?.username}`, JSON.stringify(newViewed));
                                  }
                                  goTo('tasks');
                                }}
                              >
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      {isNew && <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />}
                                      <h3 className="font-semibold text-gray-900">{task.title}</h3>
                                    </div>
                                    {task.customerName && (
                                      <p className="text-sm text-gray-600 mb-2">Customer: {task.customerName}</p>
                                    )}
                                    {task.description && (
                                      <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                                    )}
                                    <div className="flex items-center gap-3 flex-wrap">
                                      <span
                                        className={`px-2 py-1 rounded-full text-xs font-semibold ${task.priority === 'High' ? 'bg-red-100 text-red-800' :
                                          task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-green-100 text-green-800'
                                          }`}
                                      >
                                        {task.priority}
                                      </span>
                                      <span
                                        className={`px-2 py-1 rounded-full text-xs font-semibold ${task.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                          task.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                                            'bg-gray-100 text-gray-800'
                                          }`}
                                      >
                                        {task.status}
                                      </span>
                                      {task.deadline && (
                                        <span className="text-xs text-gray-500">
                                          Due: {new Date(task.deadline).toLocaleDateString()}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {task.status !== 'Completed' && (
                                      <button
                                        className={`px-3 py-1 rounded-lg text-xs font-semibold ${task.status === 'Pending'
                                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                                          : 'bg-green-600 text-white hover:bg-green-700'
                                          }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const newStatus = task.status === 'Pending' ? 'In Progress' : 'Completed';
                                          handleUpdateTaskStatus(task._id, newStatus);
                                        }}
                                      >
                                        {task.status === 'Pending' ? 'Start' : 'Complete'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                  </div>

                  {/* RIGHT COLUMN - CHARTS & METRICS (1/3 width) */}
                  <div className="lg:col-span-1 space-y-6">

                    {/* PERFORMANCE METRICS */}
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-sm p-6 text-white">
                      <h2 className="text-lg font-bold mb-4">📊 This Week</h2>
                      <div className="space-y-4">
                        <div className="bg-white/10 backdrop-blur rounded-lg p-3">
                          <div className="text-2xl font-bold">{weeklyCount}</div>
                          <div className="text-xs opacity-90">Appraisals</div>
                        </div>
                        <div className="bg-white/10 backdrop-blur rounded-lg p-3">
                          <div className="text-2xl font-bold">{avgGrade}</div>
                          <div className="text-xs opacity-90">Avg Grade</div>
                        </div>
                        <div className="bg-white/10 backdrop-blur rounded-lg p-3">
                          <div className="text-2xl font-bold">{totalExt + totalInt}</div>
                          <div className="text-xs opacity-90">Total Faults</div>
                        </div>
                        <div className="bg-white/10 backdrop-blur rounded-lg p-3">
                          <div className="text-2xl font-bold">
                            {tasks.filter(t => t.status === 'Completed').length}
                          </div>
                          <div className="text-xs opacity-90">Jobs Done</div>
                        </div>
                      </div>
                    </div>

                    {/* GRADE BREAKDOWN */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Grade Breakdown</h3>

                      {!hasAnyGrades ? (
                        <div className="text-center py-8">
                          <div className="text-4xl mb-2">📊</div>
                          <p className="text-sm text-gray-500">No grades yet</p>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-center mb-4">
                            <div className="relative" style={{ width: "140px", height: "140px" }}>
                              <div
                                style={{
                                  width: "140px",
                                  height: "140px",
                                  borderRadius: "999px",
                                  background: `conic-gradient(
                                    #22c55e 0deg ${d1}deg,
                                    #3b82f6 ${d1}deg ${d1 + d2}deg,
                                    #fbbf24 ${d1 + d2}deg ${d1 + d2 + d3}deg,
                                    #f97316 ${d1 + d2 + d3}deg ${d1 + d2 + d3 + d4}deg,
                                    #ef4444 ${d1 + d2 + d3 + d4}deg 360deg
                                  )`
                                }}
                              />
                              <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                textAlign: 'center'
                              }}>
                                <div style={{ fontSize: '28px', fontWeight: '700', color: '#000' }}>
                                  {numericGrades.length}
                                </div>
                                <div style={{ fontSize: '11px', color: '#666' }}>Graded</div>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-2">
                            {[1, 2, 3, 4, 5].map((grade) => (
                              <div key={grade} className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                  <span
                                    className="w-3 h-3 rounded"
                                    style={{
                                      background: grade === 1 ? '#22c55e' :
                                        grade === 2 ? '#3b82f6' :
                                          grade === 3 ? '#fbbf24' :
                                            grade === 4 ? '#f97316' : '#ef4444'
                                    }}
                                  />
                                  <span className="font-medium">Grade {grade}</span>
                                </div>
                                <span className="font-bold">{gradeCounts[grade]}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>



                  </div>
                </div>

                {/* EXPANDABLE APPROVAL TABLES - FULL WIDTH */}
                <div id="approval-tables-section" className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

                  {/* APPROVED APPRAISALS */}
                  <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                    <button
                      onClick={() => setFilterStatus(filterStatus === 'approved' ? null : 'approved')}
                      className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">✅</span>
                        <div className="text-left">
                          <h3 className="font-bold text-gray-900">Approved Appraisals</h3>
                          <p className="text-sm text-gray-500">
                            {appraisals.filter(a => a.approvalStatus === 'approved').length} approved
                          </p>
                        </div>
                      </div>
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${filterStatus === 'approved' ? 'rotate-180' : ''
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {filterStatus === 'approved' && (
                      <div className="border-t">
                        {(() => {
                          const approved = appraisals.filter(a => a.approvalStatus === 'approved').slice(0, 5);
                          if (approved.length === 0) {
                            return (
                              <div className="p-8 text-center text-gray-500">
                                No approved appraisals yet
                              </div>
                            );
                          }
                          return (
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">REG</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">VEHICLE</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">GRADE</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y">
                                  {approved.map((a, i) => (
                                    <tr key={i} className="hover:bg-gray-50">
                                      <td className="px-4 py-3 text-sm font-medium">{a.reg}</td>
                                      <td className="px-4 py-3 text-sm">{a.vehicle}</td>
                                      <td className="px-4 py-3">
                                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
                                          {a.overallGrade || 'N/A'}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  {/* REJECTED APPRAISALS */}
                  <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                    <button
                      onClick={() => setFilterStatus(filterStatus === 'rejected' ? null : 'rejected')}
                      className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">❌</span>
                        <div className="text-left">
                          <h3 className="font-bold text-gray-900">Rejected Appraisals</h3>
                          <p className="text-sm text-gray-500">
                            {appraisals.filter(a => a.approvalStatus === 'rejected').length} rejected
                          </p>
                        </div>
                      </div>
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${filterStatus === 'rejected' ? 'rotate-180' : ''
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {filterStatus === 'rejected' && (
                      <div className="border-t">
                        {(() => {
                          const rejected = appraisals.filter(a => a.approvalStatus === 'rejected').slice(0, 5);
                          if (rejected.length === 0) {
                            return (
                              <div className="p-8 text-center text-gray-500">
                                No rejected appraisals
                              </div>
                            );
                          }
                          return (
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">REG</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">VEHICLE</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">REASON</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y">
                                  {rejected.map((a, i) => (
                                    <tr key={i} className="hover:bg-gray-50">
                                      <td className="px-4 py-3 text-sm font-medium">{a.reg}</td>
                                      <td className="px-4 py-3 text-sm">{a.vehicle}</td>
                                      <td className="px-4 py-3 text-sm text-red-600">
                                        {a.adminMessage || "No reason"}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>


                </div>

              </>
            );
          })()}


        {
          activeSection === 'start' && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="mb-6">
                <ProgressBar currentStep={currentStep} totalSteps={7} />
              </div>

              {currentStep === 1 && (
                <div className="reg-start">
                  <img src={logo} alt="Heston Automotive" className="reg-logo" />
                  <p className="reg-sub">Enter vehicle registration number</p>
                  <form className="reg-search" onSubmit={(e) => {
                    e.preventDefault();
                    handleFindVehicle();
                  }}>
                    <input
                      className="reg-input"
                      placeholder="e.g. AB12 CDE"
                      value={form.reg}
                      onChange={(e) => setForm({ ...form, reg: e.target.value.toUpperCase() })}
                      autoFocus
                    />
                    <button className="reg-btn" type="submit" disabled={loadingVehicle}>
                      {loadingVehicle ? <FaSpinner className="spin" /> : 'Find Vehicle'}
                    </button>
                  </form>
                </div>
              )}

              {currentStep === 2 && (
                <div className="sd2-wrap">
                  <div className="card step2-card">

                    {/* Hero Section - Registration, Make/Model, Year */}
                    <div className="step2-hero">
                      <div className="step2-hero-reg">
                        <label className="step2-label-small">Registration</label>
                        <input
                          className="step2-reg-plate"
                          value={form.reg || ''}
                          onChange={(e) => setForm(p => ({ ...p, reg: e.target.value.toUpperCase() }))}
                          placeholder="Enter Reg"
                        />
                      </div>
                      <div className="step2-hero-make">
                        <div className="step2-make-text">{form.make || 'MAKE'}</div>
                        <div className="step2-model-text">{form.model || 'Model'}</div>
                      </div>
                      <div className="step2-hero-year">
                        <label className="step2-label-small">Year</label>
                        <div className="step2-year-badge">{form.year || '----'}</div>
                      </div>
                    </div>

                    {/* Quick Stats Bar */}
                    <div className="step2-stats">
                      <div className="step2-stat-item">
                        <div className="step2-stat-value">{form.mileage || '—'}</div>
                        <div className="step2-stat-label">Mileage</div>
                      </div>
                      <div className="step2-stat-item">
                        <div className="step2-stat-value">{form.fuelType || '—'}</div>
                        <div className="step2-stat-label">Fuel Type</div>
                      </div>
                      <div className="step2-stat-item">
                        <div className="step2-stat-value">{form.engineSize ? `${form.engineSize}cc` : '—'}</div>
                        <div className="step2-stat-label">Engine</div>
                      </div>
                      <div className="step2-stat-item">
                        <div className="step2-stat-value step2-colour">{form.colour || '—'}</div>
                        <div className="step2-stat-label">Colour</div>
                      </div>
                    </div>

                    {/* Section 1: Core Vehicle Information */}
                    <div className="step2-section">
                      <div className="step2-section-header">
                        <div className="step2-section-icon step2-icon-blue">🚗</div>
                        <span className="step2-section-title">Core Vehicle Information</span>
                      </div>
                      <div className="step2-grid-3">
                        <div className="f">
                          <label className="label">VIN Number</label>
                          <input
                            className="input"
                            value={form.vin || ''}
                            onChange={(e) => setForm(p => ({ ...p, vin: e.target.value }))}
                            placeholder="Enter VIN"
                          />
                        </div>
                        <div className="f">
                          <label className="label">Make</label>
                          <input
                            className="input"
                            value={form.make || ''}
                            onChange={(e) => setForm(p => ({ ...p, make: e.target.value }))}
                            placeholder="Enter make"
                          />
                        </div>
                        <div className="f">
                          <label className="label">Model</label>
                          <input
                            className="input"
                            value={form.model || ''}
                            onChange={(e) => setForm(p => ({ ...p, model: e.target.value }))}
                            placeholder="Enter model"
                          />
                        </div>
                        <div className="f">
                          <label className="label">Colour</label>
                          <input
                            className="input"
                            value={form.colour || ''}
                            onChange={(e) => setForm(p => ({ ...p, colour: e.target.value }))}
                            placeholder="Enter colour"
                          />
                        </div>
                        <div className="f">
                          <label className="label">Transmission</label>
                          <input
                            className="input"
                            value={form.transmission || ''}
                            onChange={(e) => setForm(p => ({ ...p, transmission: e.target.value }))}
                            placeholder="Manual/Automatic"
                          />
                        </div>
                        <div className="f">
                          <label className="label">Mileage</label>
                          <input
                            className="input"
                            type="number"
                            value={form.mileage || ''}
                            onChange={(e) => setForm(p => ({ ...p, mileage: e.target.value }))}
                            placeholder="Enter mileage"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Section 2: Technical Specifications */}
                    <div className="step2-section">
                      <div className="step2-section-header">
                        <div className="step2-section-icon step2-icon-orange">⚙️</div>
                        <span className="step2-section-title">Technical Specifications</span>
                      </div>
                      <div className="step2-grid-4">
                        <div className="f">
                          <label className="label">Fuel Type</label>
                          <input
                            className="input"
                            value={form.fuelType || ''}
                            onChange={(e) => setForm(p => ({ ...p, fuelType: e.target.value }))}
                            placeholder="Petrol/Diesel/Electric"
                          />
                        </div>
                        <div className="f">
                          <label className="label">Engine Size (cc)</label>
                          <input
                            className="input"
                            value={form.engineSize || ''}
                            onChange={(e) => setForm(p => ({ ...p, engineSize: e.target.value }))}
                            placeholder="Enter engine size"
                          />
                        </div>
                        <div className="f">
                          <label className="label">CO2 Emissions</label>
                          <input
                            className="input"
                            value={form.co2 || ''}
                            onChange={(e) => setForm(p => ({ ...p, co2: e.target.value }))}
                            placeholder="Enter CO2"
                          />
                        </div>
                        <div className="f">
                          <label className="label">Euro Status</label>
                          <input
                            className="input"
                            value={form.euroStatus || ''}
                            onChange={(e) => setForm(p => ({ ...p, euroStatus: e.target.value }))}
                            placeholder="Euro 6/5/4"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="step2-grid-4" style={{ marginTop: '1rem' }}>
                      <div className="f">
                        <label className="label">Revenue Weight</label>
                        <input
                          className="input"
                          value={form.revenueWeight || ''}
                          onChange={(e) => setForm(p => ({ ...p, revenueWeight: e.target.value }))}
                          placeholder="Enter weight"
                        />
                      </div>
                    </div>

                    {/* Section 3: Tax & MOT Status */}
                    <div className="step2-section">
                      <div className="step2-section-header">
                        <div className="step2-section-icon step2-icon-green">✓</div>
                        <span className="step2-section-title">Tax & MOT Status</span>
                        <div className="step2-badges">
                          {form.motStatus && (
                            <span className={`step2-badge ${form.motStatus?.toLowerCase() === 'valid' ? 'step2-badge-green' : 'step2-badge-red'}`}>
                              MOT {form.motStatus}
                            </span>
                          )}
                          {form.taxStatus && (
                            <span className={`step2-badge ${form.taxStatus?.toLowerCase() === 'taxed' ? 'step2-badge-blue' : 'step2-badge-orange'}`}>
                              {form.taxStatus}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="step2-grid-4">
                        <div className="f">
                          <label className="label">MOT Status</label>
                          <input
                            className="input"
                            value={form.motStatus || ''}
                            onChange={(e) => setForm(p => ({ ...p, motStatus: e.target.value }))}
                            placeholder="Valid/Expired"
                          />
                        </div>
                        <div className="f">
                          <label className="label">Tax Status</label>
                          <input
                            className="input"
                            value={form.taxStatus || ''}
                            onChange={(e) => setForm(p => ({ ...p, taxStatus: e.target.value }))}
                            placeholder="Taxed/SORN"
                          />
                        </div>
                        <div className="f">
                          <label className="label">Tax Due Date</label>
                          <input
                            className="input"
                            type="date"
                            value={form.taxDueDate || ''}
                            onChange={(e) => setForm(p => ({ ...p, taxDueDate: e.target.value }))}
                          />
                        </div>
                        {/* Moved Revenue Weight to Tech Specs */}
                      </div>
                    </div>




                    {/* Section 4: Registration & Documents */}
                    <div className="step2-section step2-section-last">
                      <div className="step2-section-header">
                        <div className="step2-section-icon step2-icon-purple">📄</div>
                        <span className="step2-section-title">Registration & Documents</span>
                      </div>
                      <div className="step2-grid-4">
                        <div className="f">
                          <label className="label">Registration Date</label>
                          <input
                            className="input"
                            type="date"
                            value={form.regDate || ''}
                            onChange={(e) => setForm(p => ({ ...p, regDate: e.target.value }))}
                          />
                        </div>
                        <div className="f">
                          <label className="label">Year of Manufacture</label>
                          <input
                            className="input"
                            value={form.yearOfManufacture || ''}
                            onChange={(e) => setForm(p => ({ ...p, yearOfManufacture: e.target.value }))}
                            placeholder="Enter year"
                          />
                        </div>
                        <div className="f">
                          <label className="label">Wheelplan</label>
                          <input
                            className="input"
                            value={form.wheelplan || ''}
                            onChange={(e) => setForm(p => ({ ...p, wheelplan: e.target.value }))}
                            placeholder="2 Axle Rigid Body"
                          />
                        </div>
                        <div className="f">
                          <label className="label">Date of Last V5C Issued</label>
                          <input
                            className="input"
                            type="date"
                            value={form.dateOfLastV5CIssued || ''}
                            onChange={(e) => setForm(p => ({ ...p, dateOfLastV5CIssued: e.target.value }))}
                          />
                        </div>
                      </div>
                    </div>

                  </div>

                  <div className="sticky-actions">
                    <button type="button" className="btn btn-ghost" onClick={prevStep}>
                      ← Previous
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => {
                      if (!form.mileage) {
                        setMessage('Please enter the vehicle mileage.');
                        return;
                      }
                      nextStep();
                    }}>
                      Next Step →
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <>
                  <h2 className="step-heading">Step 3 — Exterior Inspection</h2>
                  <p className="step-subtext">
                    {exteriorFaults.length > 0
                      ? `${exteriorFaults.length} faults recorded`
                      : 'Click on the vehicle part to record a fault.'}
                  </p>

                  <div className="mapping-layout">
                    {/* Desktop: Normal map view */}
                    <div className="mapping-left desktop-only-map">
                      <ExteriorMap
                        key={exteriorFaults.map(f => f.idx).join(',')}
                        onPartSelect={(fault) => handleAddFault(fault, 'exterior')}
                        faults={exteriorFaults}
                        onFaultPointClick={(i) => handleFaultPointClick(i, 'exterior')}
                        onFaultPointDelete={(i) => handleFaultPointDelete(i, 'exterior')}
                        onFaultUpdate={(i, coords) => handleFaultUpdate(i, coords, 'exterior')}
                      />
                    </div>

                    {/* Mobile: Desktop image rotated vertical - Full Screen */}
                    <div className="mobile-only-element" style={{ width: '100vw', marginLeft: 'calc(-50vw + 50%)', marginBottom: '20px', overflowX: 'hidden' }}>
                      <div style={{ textAlign: 'center', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                        Exterior Inspection Image
                      </div>
                      <div style={{ textAlign: 'center', marginBottom: '12px', fontSize: '12px', color: '#6b7280' }}>
                        {exteriorFaults.length > 0
                          ? `${exteriorFaults.length} faults marked`
                          : 'Tap on vehicle parts to mark faults'}
                      </div>
                      <div style={{
                        width: '180vw',
                        minHeight: '80vh',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                        overflow: 'visible'
                      }}>
                        <div style={{
                          transform: 'rotate(90deg)',
                          transformOrigin: 'center center',
                          width: '129vh',
                          height: '15vw'
                        }}>
                          <ExteriorMap
                            key={exteriorFaults.map(f => f.idx).join(',')}
                            onPartSelect={(fault) => handleAddFault(fault, 'exterior')}
                            faults={exteriorFaults}
                            onFaultPointClick={(i) => handleFaultPointClick(i, 'exterior')}
                            onFaultPointDelete={(i) => handleFaultPointDelete(i, 'exterior')}
                            onFaultUpdate={(i, coords) => handleFaultUpdate(i, coords, 'exterior')}
                            mobileRotated={true}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="mapping-right">
                      <div className="step-navigation">
                        <button className="btn" onClick={prevStep}>Previous</button>
                        <button className="btn btn-primary" onClick={nextStep}>Next Step</button>
                        <button
                          className="btn"
                          onClick={() => {
                            if (exteriorFaults.length > 0 &&
                              !window.confirm('This will clear all exterior faults. Continue?'))
                              return;
                            setExteriorFaults([]);
                            setMessage('No exterior faults recorded.');
                            nextStep();
                          }}
                        >
                          No Exterior Faults
                        </button>
                      </div>

                      {/* Exterior Faults List with Edit/Delete */}
                      <div className="fault-list-section" style={{ marginTop: '20px' }}>
                        {exteriorFaults.length === 0 ? (
                          <p style={{ color: '#6b7280', fontSize: '14px', textAlign: 'center' }}>No Exterior Faults</p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {exteriorFaults.map((fault, index) => (
                              <div
                                key={index}
                                style={{
                                  background: '#f9fafb',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  padding: '12px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>
                                    {fault.idx}. {fault.part}
                                  </div>
                                  <div style={{ color: '#6b7280', fontSize: '13px' }}>
                                    {fault.damage}{fault.detail ? ` - ${fault.detail}` : ''}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button
                                    className="btn"
                                    style={{
                                      padding: '6px 12px',
                                      fontSize: '12px',
                                      background: '#fef3c7',
                                      color: '#92400e',
                                      border: '1px solid #fcd34d'
                                    }}
                                    onClick={() => {
                                      setEditFaultIndex(index);
                                      setEditFaultType('exterior');
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="btn"
                                    style={{
                                      padding: '6px 12px',
                                      fontSize: '12px',
                                      background: '#fee2e2',
                                      color: '#dc2626',
                                      border: '1px solid #fca5a5'
                                    }}
                                    onClick={() => {
                                      if (window.confirm('Delete this fault?')) {
                                        handleDeleteFault(index, 'exterior');
                                      }
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {editFaultIndex !== null && editFaultType === 'exterior' && (
                    <div className="sub-part-backdrop" onClick={() => { setEditFaultIndex(null); setEditFaultType(null); }}>
                      <div className="sub-part-popup" onClick={(e) => e.stopPropagation()}>
                        <h4>Edit Exterior Fault</h4>
                        {(() => {
                          const f = exteriorFaults[editFaultIndex] || {};
                          return (
                            <>
                              <input
                                value={f.part || ''}
                                onChange={(e) => {
                                  const updated = [...exteriorFaults];
                                  updated[editFaultIndex] = { ...f, part: e.target.value };
                                  setExteriorFaults(updated);
                                }}
                                placeholder="Part"
                              />
                              <input
                                value={f.damage || ''}
                                onChange={(e) => {
                                  const updated = [...exteriorFaults];
                                  updated[editFaultIndex] = { ...f, damage: e.target.value };
                                  setExteriorFaults(updated);
                                }}
                                placeholder="Damage"
                              />
                              <input
                                value={f.detail || ''}
                                onChange={(e) => {
                                  const updated = [...exteriorFaults];
                                  updated[editFaultIndex] = { ...f, detail: e.target.value };
                                  setExteriorFaults(updated);
                                }}
                                placeholder="Detail"
                              />
                              <div className="popup-actions">
                                <button className="btn btn-primary" onClick={() => { setEditFaultIndex(null); setEditFaultType(null); }}>
                                  Save
                                </button>
                                <button
                                  className="btn confirm-delete"
                                  onClick={() => {
                                    handleFaultPointDelete(editFaultIndex, 'exterior');
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </>
              )}

              {currentStep === 4 && (
                <>
                  <h2 className="step-heading">Step 4 — Interior Inspection</h2>
                  <p className="step-subtext">
                    {interiorFaults.length > 0
                      ? `${interiorFaults.length} faults recorded`
                      : 'Click on the interior part to record a fault.'}
                  </p>

                  <div className="mapping-layout">
                    {/* Desktop: Normal map view */}
                    <div className="mapping-left desktop-only-map">
                      <InteriorMap
                        key={interiorFaults.map(f => f.idx).join(',')}
                        onPartSelect={(fault) => handleAddFault(fault, 'interior')}
                        faults={interiorFaults}
                        onFaultPointClick={(i) => handleFaultPointClick(i, 'interior')}
                        onFaultPointDelete={(i) => handleFaultPointDelete(i, 'interior')}
                        onFaultUpdate={(i, coords) => handleFaultUpdate(i, coords, 'interior')}
                      />
                    </div>

                    {/* Mobile: Preview card with expand button */}
                    {/* Mobile: Desktop Interior Map Rotated Vertical - Full Screen */}
                    <div className="mobile-only-element" style={{ width: '100vw', marginLeft: 'calc(-50vw + 50%)', marginBottom: '20px', overflowX: 'hidden' }}>
                      <div style={{ textAlign: 'center', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                        Interior Inspection Image
                      </div>
                      <div style={{ textAlign: 'center', marginBottom: '12px', fontSize: '12px', color: '#6b7280' }}>
                        {interiorFaults.length > 0
                          ? `${interiorFaults.length} faults marked`
                          : 'Tap on vehicle parts to mark faults'}
                      </div>
                      <div style={{
                        width: '160vw',
                        minHeight: '80vh',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                        overflow: 'visible'
                      }}>
                        <div style={{
                          transform: 'rotate(90deg)',
                          transformOrigin: 'center center',
                          width: '129vh',
                          height: '38vw'
                        }}>
                          <InteriorMap
                            key={interiorFaults.map(f => f.idx).join(',')}
                            onPartSelect={(fault) => handleAddFault(fault, 'interior')}
                            faults={interiorFaults}
                            onFaultPointClick={(i) => handleFaultPointClick(i, 'interior')}
                            onFaultPointDelete={(i) => handleFaultPointDelete(i, 'interior')}
                            onFaultUpdate={(i, coords) => handleFaultUpdate(i, coords, 'interior')}
                            mobileRotated={true}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="mapping-right">
                      <div className="step-navigation">
                        <button className="btn" onClick={prevStep}>Previous</button>
                        <button className="btn btn-primary" onClick={nextStep}>Next Step</button>
                        <button
                          className="btn"
                          onClick={() => {
                            if (interiorFaults.length > 0 &&
                              !window.confirm('This will clear all interior faults. Continue?'))
                              return;
                            setInteriorFaults([]);
                            setMessage('No interior faults recorded.');
                            nextStep();
                          }}
                        >
                          No Interior Faults
                        </button>
                      </div>

                      {/* Interior Faults List with Edit/Delete */}
                      <div className="fault-list-section" style={{ marginTop: '20px' }}>
                        {interiorFaults.length === 0 ? (
                          <p style={{ color: '#6b7280', fontSize: '14px', textAlign: 'center' }}>No Interior Faults</p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {interiorFaults.map((fault, index) => (
                              <div
                                key={index}
                                style={{
                                  background: '#f9fafb',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  padding: '12px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>
                                    {fault.idx}. {fault.part}
                                  </div>
                                  <div style={{ color: '#6b7280', fontSize: '13px' }}>
                                    {fault.damage}{fault.detail ? ` - ${fault.detail}` : ''}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button
                                    className="btn"
                                    style={{
                                      padding: '6px 12px',
                                      fontSize: '12px',
                                      background: '#fef3c7',
                                      color: '#92400e',
                                      border: '1px solid #fcd34d'
                                    }}
                                    onClick={() => {
                                      setEditFaultIndex(index);
                                      setEditFaultType('interior');
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="btn"
                                    style={{
                                      padding: '6px 12px',
                                      fontSize: '12px',
                                      background: '#fee2e2',
                                      color: '#dc2626',
                                      border: '1px solid #fca5a5'
                                    }}
                                    onClick={() => {
                                      if (window.confirm('Delete this fault?')) {
                                        handleDeleteFault(index, 'interior');
                                      }
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {editFaultIndex !== null && editFaultType === 'interior' && (
                    <div className="sub-part-backdrop" onClick={() => { setEditFaultIndex(null); setEditFaultType(null); }}>
                      <div className="sub-part-popup" onClick={(e) => e.stopPropagation()}>
                        <h4>Edit Interior Fault</h4>
                        {(() => {
                          const f = interiorFaults[editFaultIndex] || {};
                          return (
                            <>
                              <input
                                value={f.part || ''}
                                onChange={(e) => {
                                  const updated = [...interiorFaults];
                                  updated[editFaultIndex] = { ...f, part: e.target.value };
                                  setInteriorFaults(updated);
                                }}
                                placeholder="Part"
                              />
                              <input
                                value={f.damage || ''}
                                onChange={(e) => {
                                  const updated = [...interiorFaults];
                                  updated[editFaultIndex] = { ...f, damage: e.target.value };
                                  setInteriorFaults(updated);
                                }}
                                placeholder="Damage"
                              />
                              <input
                                value={f.detail || ''}
                                onChange={(e) => {
                                  const updated = [...interiorFaults];
                                  updated[editFaultIndex] = { ...f, detail: e.target.value };
                                  setInteriorFaults(updated);
                                }}
                                placeholder="Detail"
                              />
                              <div className="popup-actions">
                                <button className="btn btn-primary" onClick={() => { setEditFaultIndex(null); setEditFaultType(null); }}>
                                  Save
                                </button>
                                <button
                                  className="btn confirm-delete"
                                  onClick={() => {
                                    handleFaultPointDelete(editFaultIndex, 'interior');
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </>
              )}

              {currentStep === 5 && (
                <>
                  <h2 className="step-heading">Step 5 — Tyres, Wheels & Mechanical Check</h2>

                  {/* TYRES SECTION */}
                  <h3 style={{ marginTop: '20px', marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>🚗 Tyre & Wheel Inspection</h3>
                  <p className="step-subtext">Click a tyre on the image and fill the mini form.</p>

                  <div className="mapping-layout">
                    <div className="mapping-left">
                      <TyreOverlaySVG
                        form={form}
                        setForm={setForm}
                        onBonnetClick={() => setShowMechanicalPopup(true)}
                      />
                    </div>

                    <div className="mapping-right">
                      <div style={{
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '16px'
                      }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Tyre Summary</h4>
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>
                          {form.tyres?.filter(t => t.treadDepth || t.condition).length || 0} of 4 tyres checked
                        </div>
                      </div>

                      {/* Mechanical Checks Info Box */}
                      <div style={{
                        background: '#fef2f2',
                        border: '2px dashed #ef4444',
                        borderRadius: '8px',
                        padding: '16px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <span style={{ fontSize: '20px' }}>🔧</span>
                          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#991b1b', margin: 0 }}>
                            Mechanical Checks
                          </h4>
                        </div>
                        <p style={{ fontSize: '12px', color: '#7f1d1d', margin: 0, marginBottom: '8px' }}>
                          Click on the bonnet (red area) to fill out mechanical & fluid checks
                        </p>
                        <div style={{ fontSize: '12px', color: '#991b1b' }}>
                          {(form.oilLeakage || form.coolantLevel || form.brakeFluidLevel || form.warningLights || form.batteryCondition || form.exhaustCondition) ? (
                            <span style={{ fontWeight: '600' }}>✓ Checks completed</span>
                          ) : (
                            <span>⚠️ Not started</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* MECHANICAL CHECKS POPUP MODAL */}
                  {showMechanicalPopup && (
                    <div className="step6-popup-overlay" onClick={() => setShowMechanicalPopup(false)}>
                      <div className="step6-popup-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="step6-popup-header">
                          <h2>🔧 Mechanical & Fluid Checks</h2>
                          <button className="step6-popup-close" onClick={() => setShowMechanicalPopup(false)}>×</button>
                        </div>
                        <div className="step6-popup-content">

                          {/* Oil Leakage + Color */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>🛢️</span>
                              <h3>Oil</h3>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                              <select
                                className="input"
                                style={{ flex: 1, minWidth: '140px' }}
                                value={form.oilLeakage || ''}
                                onChange={(e) => setForm({ ...form, oilLeakage: e.target.value })}
                              >
                                <option value="">Not Checked</option>
                                <option value="none">✓ No Leakage</option>
                                <option value="minor">⚠️ Minor Seepage</option>
                                <option value="severe">❌ Severe Leak</option>
                              </select>
                              <select
                                className="input"
                                style={{ flex: 1, minWidth: '140px' }}
                                value={form.oilColor || ''}
                                onChange={(e) => setForm({ ...form, oilColor: e.target.value })}
                              >
                                <option value="">Color - Not Checked</option>
                                <option value="amber">🟡 Amber (Good)</option>
                                <option value="dark-brown">🟤 Dark Brown (Due Change)</option>
                                <option value="black">⚫ Black (Change Now)</option>
                                <option value="dirty">⚠️ Dirty/Cloudy</option>
                                <option value="faded">⚠️ Faded/Discolored</option>
                                <option value="unknown">❓ Unknown/Other</option>
                              </select>
                            </div>

                            {/* Oil Level Slider */}
                            <div style={{ marginTop: '12px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>Oil Level:</span>
                                <span style={{
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  color: (form.oilLevel || 0) >= 75 ? '#16a34a' : (form.oilLevel || 0) >= 25 ? '#f59e0b' : '#ef4444'
                                }}>
                                  {form.oilLevel != null ? `${form.oilLevel}%` : 'Not Checked'}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '11px', color: '#9ca3af' }}>Empty</span>
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  step="5"
                                  value={form.oilLevel || 0}
                                  onChange={(e) => setForm({ ...form, oilLevel: parseInt(e.target.value) })}
                                  style={{
                                    flex: 1,
                                    height: '8px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    accentColor: (form.oilLevel || 0) >= 75 ? '#16a34a' : (form.oilLevel || 0) >= 25 ? '#f59e0b' : '#ef4444'
                                  }}
                                />
                                <span style={{ fontSize: '11px', color: '#9ca3af' }}>Full</span>
                              </div>
                            </div>
                          </div>

                          {/* Coolant Level + Color */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>🌡️</span>
                              <h3>Coolant</h3>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                              <select
                                className="input"
                                style={{ flex: 1, minWidth: '140px' }}
                                value={form.coolantLevel || ''}
                                onChange={(e) => setForm({ ...form, coolantLevel: e.target.value })}
                              >
                                <option value="">Not Checked</option>
                                <option value="good">✓ Good/Full</option>
                                <option value="low">⚠️ Low</option>
                                <option value="empty">❌ Empty</option>
                              </select>
                              <select
                                className="input"
                                style={{ flex: 1, minWidth: '140px' }}
                                value={form.coolantColor || ''}
                                onChange={(e) => setForm({ ...form, coolantColor: e.target.value })}
                              >
                                <option value="">Color - Not Checked</option>
                                <option value="green">🟢 Green (Good)</option>
                                <option value="blue">🔵 Blue (Good)</option>
                                <option value="pink">🩷 Pink/Red (Good)</option>
                                <option value="orange">🟠 Orange (Good)</option>
                                <option value="rusty">🟤 Rusty (Contaminated)</option>
                                <option value="dirty">⚠️ Dirty/Cloudy</option>
                                <option value="faded">⚠️ Faded/Discolored</option>
                                <option value="unknown">❓ Unknown/Other</option>
                              </select>
                            </div>

                            {/* Coolant Level Slider */}
                            <div style={{ marginTop: '12px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>Coolant Level:</span>
                                <span style={{
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  color: (form.coolantLevelPercent || 0) >= 75 ? '#16a34a' : (form.coolantLevelPercent || 0) >= 25 ? '#f59e0b' : '#ef4444'
                                }}>
                                  {form.coolantLevelPercent != null ? `${form.coolantLevelPercent}%` : 'Not Checked'}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '11px', color: '#9ca3af' }}>Empty</span>
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  step="5"
                                  value={form.coolantLevelPercent || 0}
                                  onChange={(e) => setForm({ ...form, coolantLevelPercent: parseInt(e.target.value) })}
                                  style={{
                                    flex: 1,
                                    height: '8px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    accentColor: (form.coolantLevelPercent || 0) >= 75 ? '#16a34a' : (form.coolantLevelPercent || 0) >= 25 ? '#f59e0b' : '#ef4444'
                                  }}
                                />
                                <span style={{ fontSize: '11px', color: '#9ca3af' }}>Full</span>
                              </div>
                            </div>
                          </div>

                          {/* Brake Fluid */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>🛑</span>
                              <h3>Brake Fluid Level</h3>
                            </div>
                            <select
                              className="input"
                              value={form.brakeFluidLevel || ''}
                              onChange={(e) => setForm({ ...form, brakeFluidLevel: e.target.value })}
                            >
                              <option value="">Not Checked</option>
                              <option value="good">✓ Good</option>
                              <option value="low">⚠️ Low</option>
                            </select>
                          </div>

                          {/* Warning Lights */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>⚠️</span>
                              <h3>Any Warning Lights / Messages</h3>
                            </div>
                            <input
                              className="input"
                              type="text"
                              placeholder="e.g., Check Engine Light, ABS Warning, etc."
                              value={form.warningLights || ''}
                              onChange={(e) => setForm({ ...form, warningLights: e.target.value })}
                            />
                          </div>

                          {/* Battery Condition */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>🔋</span>
                              <h3>Battery Condition</h3>
                            </div>
                            <select
                              className="input"
                              value={form.batteryCondition || ''}
                              onChange={(e) => setForm({ ...form, batteryCondition: e.target.value })}
                            >
                              <option value="">Not Checked</option>
                              <option value="good">✓ Good</option>
                              <option value="weak">⚠️ Weak</option>
                              <option value="replace">❌ Needs Replacement</option>
                            </select>
                          </div>

                          {/* Exhaust Condition */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>💨</span>
                              <h3>Exhaust Condition</h3>
                            </div>
                            <select
                              className="input"
                              value={form.exhaustCondition || ''}
                              onChange={(e) => setForm({ ...form, exhaustCondition: e.target.value })}
                            >
                              <option value="">Not Checked</option>
                              <option value="good">✓ Good</option>
                              <option value="rust">⚠️ Surface Rust</option>
                              <option value="damaged">❌ Damaged/Corroded</option>
                            </select>
                          </div>

                          {/* Mechanical Notes */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>📝</span>
                              <h3>Additional Mechanical Notes</h3>
                            </div>
                            <textarea
                              className="textarea-input"
                              rows="4"
                              placeholder="Any other mechanical observations, issues, or comments..."
                              value={form.mechanicalNotes || ''}
                              onChange={(e) => setForm({ ...form, mechanicalNotes: e.target.value })}
                            />
                          </div>

                        </div>
                        <div className="step6-popup-footer">
                          <button className="btn btn-ghost" onClick={() => setShowMechanicalPopup(false)}>Cancel</button>
                          <button className="btn btn-primary" onClick={() => {
                            setShowMechanicalPopup(false);
                            setMessage('✅ Mechanical checks saved successfully!');
                          }}>Save Checks</button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="step-navigation">
                    <button className="btn" onClick={prevStep}>Previous</button>
                    <button className="btn btn-primary" onClick={nextStep}>Next Step</button>
                  </div>
                </>
              )}

              {currentStep === 6 && (
                <>
                  <h2 className="step-heading">Step 6 – Service, MOT & Additional Info</h2>

                  {/* CLICKABLE CARDS */}
                  <div className="step6-cards-container">
                    {/* Details Card */}
                    <div
                      className="step6-clickable-card"
                      onClick={() => setShowDetailsPopup(true)}
                    >
                      <div className="step6-card-header">
                        <span className="step6-card-icon">📋</span>
                        <h3>Details</h3>
                        <span className="step6-card-arrow">→</span>
                      </div>
                      <p className="step6-card-desc">Vehicle mileage, owners, keys, V5 document & write-off info</p>
                      <div className="step6-card-status">
                        {(form.vehicleMileage || form.numberOfOwners || form.numberOfKeys) ? (
                          <span className="status-filled">✓ Partially Filled</span>
                        ) : (
                          <span className="status-empty">⚠️ Not Started</span>
                        )}
                      </div>
                    </div>

                    {/* Service, MOT & Additional Info Card */}
                    <div
                      className="step6-clickable-card"
                      onClick={() => setShowServicePopup(true)}
                    >
                      <div className="step6-card-header">
                        <span className="step6-card-icon">🔧</span>
                        <h3>Service, MOT & Additional Info</h3>
                        <span className="step6-card-arrow">→</span>
                      </div>
                      <p className="step6-card-desc">Service history, MOT status, usage history & notes</p>
                      <div className="step6-card-status">
                        {(form.serviceHistoryType || form.motExpiryDate || form.additionalNotes) ? (
                          <span className="status-filled">✓ Partially Filled</span>
                        ) : (
                          <span className="status-empty">⚠️ Not Started</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="step-navigation">
                    <button className="btn btn-ghost" onClick={prevStep}>Previous</button>
                    <button className="btn btn-primary" onClick={nextStep}>Next Step</button>
                  </div>

                  {/* DETAILS POPUP MODAL */}
                  {showDetailsPopup && (
                    <div className="step6-popup-overlay" onClick={() => setShowDetailsPopup(false)}>
                      <div className="step6-popup-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="step6-popup-header">
                          <h2>📋 Details</h2>
                          <button className="step6-popup-close" onClick={() => setShowDetailsPopup(false)}>×</button>
                        </div>
                        <div className="step6-popup-content">
                          {/* Vehicle Mileage */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>⚠️</span>
                              <h3>Vehicle Mileage</h3>
                            </div>
                            <div className="step6-popup-field">
                              <label>Enter Mileage</label>
                              <input
                                className="input"
                                type="number"
                                placeholder="Enter Mileage"
                                value={form.vehicleMileage}
                                onChange={(e) => setForm({ ...form, vehicleMileage: e.target.value })}
                              />
                            </div>
                          </div>

                          {/* Mileage Modifications */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>⚠️</span>
                              <h3>Is the customer aware of any mileage modifications?</h3>
                            </div>
                            <div className="step6-popup-buttons">
                              <button
                                className={`btn ${form.mileageModifications === 'yes' ? 'btn-primary' : 'btn-ghost'}`}
                                onClick={() => setForm({ ...form, mileageModifications: 'yes' })}
                              >
                                Yes
                              </button>
                              <button
                                className={`btn ${form.mileageModifications === 'no' ? 'btn-primary' : 'btn-ghost'}`}
                                onClick={() => setForm({ ...form, mileageModifications: 'no' })}
                              >
                                No
                              </button>
                            </div>
                          </div>

                          {/* V5 Document */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>⚠️</span>
                              <h3>Does the customer have the V5 document?</h3>
                            </div>
                            <div className="step6-popup-buttons">
                              <button
                                className={`btn ${form.hasV5Document === 'yes' ? 'btn-primary' : 'btn-ghost'}`}
                                onClick={() => setForm({ ...form, hasV5Document: 'yes' })}
                              >
                                Yes
                              </button>
                              <button
                                className={`btn ${form.hasV5Document === 'no' ? 'btn-primary' : 'btn-ghost'}`}
                                onClick={() => setForm({ ...form, hasV5Document: 'no' })}
                              >
                                No
                              </button>
                            </div>
                          </div>

                          {/* Number of Owners */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>⚠️</span>
                              <h3>Number of owners</h3>
                            </div>
                            <select
                              className="input"
                              value={form.numberOfOwners}
                              onChange={(e) => setForm({ ...form, numberOfOwners: e.target.value })}
                            >
                              <option value="">Select Owners</option>
                              <option value="1">1 Owner</option>
                              <option value="2">2 Owners</option>
                              <option value="3">3 Owners</option>
                              <option value="4">4 Owners</option>
                              <option value="5+">5+ Owners</option>
                            </select>
                          </div>

                          {/* Number of Keys */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>⚠️</span>
                              <h3>Number of keys</h3>
                            </div>
                            <div className="step6-popup-buttons">
                              <button
                                className={`btn ${form.numberOfKeys === '1' ? 'btn-primary' : 'btn-ghost'}`}
                                onClick={() => setForm({ ...form, numberOfKeys: '1' })}
                              >
                                1
                              </button>
                              <button
                                className={`btn ${form.numberOfKeys === '2' ? 'btn-primary' : 'btn-ghost'}`}
                                onClick={() => setForm({ ...form, numberOfKeys: '2' })}
                              >
                                2
                              </button>
                              <button
                                className={`btn ${form.numberOfKeys === '3+' ? 'btn-primary' : 'btn-ghost'}`}
                                onClick={() => setForm({ ...form, numberOfKeys: '3+' })}
                              >
                                3 or More
                              </button>
                            </div>
                          </div>

                          {/* First Registered in UK */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>⚠️</span>
                              <h3>Was the vehicle first registered in the UK?</h3>
                            </div>
                            <div className="step6-popup-buttons">
                              <button
                                className={`btn ${form.firstRegisteredUK === 'yes' ? 'btn-primary' : 'btn-ghost'}`}
                                onClick={() => setForm({ ...form, firstRegisteredUK: 'yes' })}
                              >
                                Yes
                              </button>
                              <button
                                className={`btn ${form.firstRegisteredUK === 'no' ? 'btn-primary' : 'btn-ghost'}`}
                                onClick={() => setForm({ ...form, firstRegisteredUK: 'no' })}
                              >
                                No
                              </button>
                            </div>
                          </div>

                          {/* Insurance Write Off */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>⚠️</span>
                              <h3>Has the vehicle been an insurance write off?</h3>
                            </div>
                            <div className="step6-popup-buttons" style={{ marginBottom: form.insuranceWriteOff === 'yes' ? '12px' : '0' }}>
                              <button
                                className={`btn ${form.insuranceWriteOff === 'yes' ? 'btn-primary' : 'btn-ghost'}`}
                                onClick={() => setForm({ ...form, insuranceWriteOff: 'yes' })}
                              >
                                Yes
                              </button>
                              <button
                                className={`btn ${form.insuranceWriteOff === 'no' ? 'btn-primary' : 'btn-ghost'}`}
                                onClick={() => setForm({ ...form, insuranceWriteOff: 'no' })}
                              >
                                No
                              </button>
                            </div>
                            {form.insuranceWriteOff === 'yes' && (
                              <select
                                className="input"
                                value={form.insuranceWriteOffType}
                                onChange={(e) => setForm({ ...form, insuranceWriteOffType: e.target.value })}
                              >
                                <option value="">Select Category</option>
                                <option value="Cat A">Category A</option>
                                <option value="Cat B">Category B</option>
                                <option value="Cat C">Category C</option>
                                <option value="Cat D">Category D</option>
                                <option value="Cat S">Category S</option>
                                <option value="Cat N">Category N</option>
                              </select>
                            )}
                          </div>
                        </div>
                        <div className="step6-popup-footer">
                          <button className="btn btn-ghost" onClick={() => setShowDetailsPopup(false)}>Cancel</button>
                          <button className="btn btn-primary" onClick={() => {
                            setShowDetailsPopup(false);
                            setMessage('✅ Details saved successfully!');
                          }}>Save Details</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* SERVICE, MOT & ADDITIONAL INFO POPUP MODAL */}
                  {showServicePopup && (
                    <div className="step6-popup-overlay" onClick={() => setShowServicePopup(false)}>
                      <div className="step6-popup-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="step6-popup-header">
                          <h2>🔧 Service, MOT & Additional Info</h2>
                          <button className="step6-popup-close" onClick={() => setShowServicePopup(false)}>×</button>
                        </div>
                        <div className="step6-popup-content">
                          {/* Service History */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>⚠️</span>
                              <h3>Vehicle Service History?</h3>
                            </div>
                            <div className="step6-popup-grid-3">
                              <select
                                className="input"
                                value={form.serviceHistoryType}
                                onChange={(e) => setForm({ ...form, serviceHistoryType: e.target.value })}
                              >
                                <option value="">What Type?</option>
                                <option value="Full">Full Service History</option>
                                <option value="Partial">Partial Service History</option>
                                <option value="None">No Service History</option>
                              </select>

                              <select
                                className="input"
                                value={form.serviceHistoryCount}
                                onChange={(e) => setForm({ ...form, serviceHistoryCount: e.target.value })}
                                disabled={!form.serviceHistoryType || form.serviceHistoryType === 'None'}
                                style={{
                                  backgroundColor: (!form.serviceHistoryType || form.serviceHistoryType === 'None') ? '#e5e7eb' : 'white',
                                  cursor: (!form.serviceHistoryType || form.serviceHistoryType === 'None') ? 'not-allowed' : 'pointer'
                                }}
                              >
                                <option value="">How many?</option>
                                {[...Array(20)].map((_, i) => (
                                  <option key={i + 1} value={i + 1}>{i + 1} Records</option>
                                ))}
                              </select>

                              <select
                                className="input"
                                value={form.mainDealer}
                                onChange={(e) => setForm({ ...form, mainDealer: e.target.value })}
                                disabled={!form.serviceHistoryType || form.serviceHistoryType === 'None'}
                                style={{
                                  backgroundColor: (!form.serviceHistoryType || form.serviceHistoryType === 'None') ? '#e5e7eb' : 'white',
                                  cursor: (!form.serviceHistoryType || form.serviceHistoryType === 'None') ? 'not-allowed' : 'pointer'
                                }}
                              >
                                <option value="">Main Dealer?</option>
                                <option value="yes">Yes - Main Dealer</option>
                                <option value="no">No - Independent</option>
                              </select>
                            </div>
                          </div>

                          {/* Vehicle Usage History */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>⚠️</span>
                              <h3>Has this vehicle ever been used for private hire, taxi, police vehicle, or daily rental?</h3>
                            </div>
                            <select
                              className="input"
                              value={form.vehicleUsageHistory}
                              onChange={(e) => setForm({ ...form, vehicleUsageHistory: e.target.value })}
                            >
                              <option value="">Please select</option>
                              <option value="None">No - Private Use Only</option>
                              <option value="Private Hire">Yes - Private Hire</option>
                              <option value="Taxi">Yes - Taxi</option>
                              <option value="Police">Yes - Police Vehicle</option>
                              <option value="Daily Rental">Yes - Daily Rental</option>
                            </select>
                          </div>

                          {/* MOT Status */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>🔍</span>
                              <h3>DVSA MOT Status</h3>
                            </div>
                            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                              Expires on {form.motExpiryDate ? new Date(form.motExpiryDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                            </p>
                            <input
                              className="input"
                              type="date"
                              value={form.motExpiryDate}
                              onChange={(e) => setForm({ ...form, motExpiryDate: e.target.value })}
                            />
                          </div>

                          {/* Notes */}
                          <div className="step6-popup-section">
                            <div className="step6-popup-section-head">
                              <span>📝</span>
                              <h3>Additional Notes</h3>
                            </div>
                            <textarea
                              className="textarea-input"
                              rows="4"
                              placeholder="Add any additional notes..."
                              value={form.additionalNotes}
                              onChange={(e) => setForm({ ...form, additionalNotes: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="step6-popup-footer">
                          <button className="btn btn-ghost" onClick={() => setShowServicePopup(false)}>Cancel</button>
                          <button className="btn btn-primary" onClick={() => {
                            setShowServicePopup(false);
                            setMessage('✅ Service info saved successfully!');
                          }}>Save Info</button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}



              {currentStep === 7 && (
                <>
                  <h2 className="step-heading">Step 7 — Summary & Finalize</h2>

                  <h4 style={{ marginBottom: '16px', fontWeight: 600 }}>Appraisal Summary</h4>
                  <div className="summary-table">
                    <table>
                      <tbody>
                        <tr><th>Exterior Faults</th><td>{exteriorFaults.length}</td></tr>
                        <tr><th>Interior Faults</th><td>{interiorFaults.length}</td></tr>
                        <tr>
                          <th>Tyre/Wheel Checks</th>
                          <td>{form.tyres.filter(t => t.treadDepth || t.condition).length} recorded</td>
                        </tr>
                        <tr><th>Overall Grade</th><td>{form.overallGrade || 'N/A'}</td></tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="step-navigation">
                    <button className="btn" onClick={prevStep}>Previous</button>
                    <button
                      className="btn submit-appraisal-btn"
                      onClick={handleFinalSubmit}
                      disabled={submitLoading}
                    >
                      {submitLoading ? <span className="dot-loader"></span> : 'Submit Appraisal'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )
        }

        {
          activeSection === 'submitted' && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-semibold mb-4">My Submitted Appraisals</h2>

              {/* DESKTOP: Table View (hidden on mobile) */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-900 font-bold">
                      <th className="text-left py-2 px-3">Registration</th>
                      <th className="text-left py-2 px-3">Vehicle</th>
                      <th className="text-left py-2 px-3">Date</th>
                      <th className="text-left py-2 px-3 hidden md:table-cell">Grade</th>
                      <th className="text-left py-2 px-3 hidden lg:table-cell">Status</th>
                      <th className="text-center py-2 px-3 hidden sm:table-cell">Folder</th>
                      <th className="text-right py-2 px-3">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {appraisals.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-6 text-center text-gray-900">
                          No appraisals found.
                        </td>
                      </tr>
                    ) : (
                      appraisals.map((a, i) => (
                        <tr key={i} className="border-t">
                          <td className="py-2 px-3 text-gray-900 font-medium">{a.reg}</td>
                          <td className="py-2 px-3 text-gray-900">{a.vehicle}</td>
                          <td className="py-2 px-3 text-gray-900">{a.date}</td>
                          <td className="py-2 px-3 hidden md:table-cell">
                            <span className="inline-block px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-semibold">
                              {a.overallGrade || 'N/A'}
                            </span>
                          </td>
                          <td className="py-2 px-3 hidden lg:table-cell">
                            <div>
                              <span
                                className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${a.approvalStatus === 'approved'
                                  ? 'bg-green-100 text-green-800'
                                  : a.approvalStatus === 'rejected'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                  }`}
                              >
                                {a.approvalStatus === 'approved'
                                  ? '✓ Approved'
                                  : a.approvalStatus === 'rejected'
                                    ? '✗ Rejected'
                                    : '⏳ Pending'}
                              </span>
                              {a.adminMessage && (
                                <div
                                  className="text-xs text-gray-600 mt-1 max-w-xs truncate"
                                  title={a.adminMessage}
                                >
                                  💬 {a.adminMessage}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-3 text-center hidden sm:table-cell">
                            <FolderButton
                              regNumber={a.reg}
                              hasDocuments={uploadedDocuments.some(d => d.regNumber === a.reg)}
                              onClick={handleFolderClick}
                              onAdd={handleAddDocuments}
                            />
                          </td>
                          <td className="py-2 px-3 text-right">
                            <div className="inline-flex gap-2">
                              <button
                                className="px-3 py-1 rounded-lg bg-blue-50 text-blue-600 text-xs"
                                onClick={async () => {
                                  try {
                                    const freshData = await api.getAppraisalById(a._id);
                                    const dataToUse = freshData || a;
                                    generatePDF(dataToUse, { mode: 'preview' });
                                    setPdfFor(dataToUse);
                                  } catch (err) {
                                    console.error('Failed to fetch fresh data:', err);
                                    generatePDF(a, { mode: 'preview' });
                                    setPdfFor(a);
                                  }
                                }}
                              >
                                View
                              </button>
                              <button
                                className="px-3 py-1 rounded-lg bg-yellow-50 text-yellow-700 hidden md:inline-block text-xs"
                                onClick={() => {
                                  if (a.deviceType === 'mobile') {
                                    setMessage('⚠️ This appraisal was created on mobile. Please use a mobile device to edit it.');
                                    return;
                                  }
                                  setIsEditMode(true);
                                  setForm(a);
                                  setExteriorFaults(a.faults?.exterior || []);
                                  setInteriorFaults(a.faults?.interior || []);
                                  setCurrentStep(2);
                                  setActiveSection('start');
                                  if (user?.username) {
                                    localStorage.removeItem(`currentAppraisalForm_${user.username}`);
                                    localStorage.removeItem(`currentExteriorFaults_${user.username}`);
                                    localStorage.removeItem(`currentInteriorFaults_${user.username}`);
                                    localStorage.setItem(`currentAppraisalStep_${user.username}`, '2');
                                  }
                                  setTimeout(() => setIsEditMode(false), 1000); // Why is this timeout here? Keeping it as is.
                                }}
                              >
                                Edit
                              </button>
                              <button
                                className="px-3 py-1 rounded-lg bg-red-50 text-red-600 text-xs"
                                onClick={() => deleteAppraisal(i)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* MOBILE: Card View (hidden on desktop) */}
              <div className="block sm:hidden space-y-4">
                {appraisals.length === 0 ? (
                  <div className="py-8 text-center text-gray-500">
                    No appraisals found.
                  </div>
                ) : (
                  appraisals.map((a, i) => (
                    <div key={i} className="appraisal-card border rounded-lg p-4 shadow-sm">
                      {/* Card Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900">{a.reg}</h3>
                          <p className="text-sm text-gray-600">{a.vehicle}</p>
                        </div>
                        <FolderButton
                          regNumber={a.reg}
                          hasDocuments={uploadedDocuments.some(d => d.regNumber === a.reg)}
                          onClick={handleFolderClick}
                          onAdd={handleAddDocuments}
                        />
                      </div>

                      {/* Card Body */}
                      <div className="space-y-2 mb-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Date:</span>
                          <span className="font-medium text-gray-900">{a.date}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Grade:</span>
                          <span className="inline-block px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-semibold">
                            {a.overallGrade || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Status:</span>
                          <span
                            className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${a.approvalStatus === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : a.approvalStatus === 'rejected'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                              }`}
                          >
                            {a.approvalStatus === 'approved'
                              ? '✓ Approved'
                              : a.approvalStatus === 'rejected'
                                ? '✗ Rejected'
                                : '⏳ Pending'}
                          </span>
                        </div>
                        {a.adminMessage && (
                          <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                            💬 {a.adminMessage}
                          </div>
                        )}
                      </div>

                      {/* Card Actions */}
                      <div className="flex gap-2 pt-3 border-t">
                        <button
                          className="flex-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium"
                          onClick={async () => {
                            try {
                              const freshData = await api.getAppraisalById(a._id);
                              const dataToUse = freshData || a;
                              generatePDF(dataToUse, { mode: 'preview' });
                              setPdfFor(dataToUse);
                            } catch (err) {
                              console.error('Failed to fetch fresh data:', err);
                              generatePDF(a, { mode: 'preview' });
                              setPdfFor(a);
                            }
                          }}
                        >
                          View
                        </button>
                        <button
                          className="flex-1 px-3 py-2 rounded-lg bg-yellow-50 text-yellow-700 text-sm font-medium border border-yellow-200"
                          onClick={() => {
                            setIsEditMode(true);
                            setForm(a);
                            setExteriorFaults(a.faults?.exterior || []);
                            setInteriorFaults(a.faults?.interior || []);
                            setCurrentStep(2);
                            setActiveSection('start');
                            if (user?.username) {
                              localStorage.removeItem(`currentAppraisalForm_${user.username}`);
                              localStorage.removeItem(`currentExteriorFaults_${user.username}`);
                              localStorage.removeItem(`currentInteriorFaults_${user.username}`);
                              localStorage.setItem(`currentAppraisalStep_${user.username}`, '2');
                            }
                            setTimeout(() => setIsEditMode(false), 1000);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="px-3 py-2 rounded-lg bg-red-50 text-red-600 text-sm font-medium border border-red-200"
                          onClick={() => deleteAppraisal(i)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )
        }

        {
          activeSection === 'search' && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-lg font-semibold mb-4">Search Appraisals</h2>
              <div className="flex gap-2 mb-6">
                <input
                  type="text"
                  className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Search by Registration, Vehicle, or Owner"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                <button
                  onClick={handleSearch}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Search
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-900 font-bold">
                      <th className="text-left py-2 px-3">Registration</th>
                      <th className="text-left py-2 px-3">Vehicle</th>
                      <th className="text-left py-2 px-3">Owner</th>
                      <th className="text-left py-2 px-3">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.length === 0 && searchQuery ? (
                      <tr>
                        <td colSpan={4} className="py-6 text-center text-gray-900">
                          No results found for "{searchQuery}".
                        </td>
                      </tr>
                    ) : searchResults.length === 0 && !searchQuery ? (
                      <tr>
                        <td colSpan={4} className="py-6 text-center text-gray-900">
                          Enter a keyword to search.
                        </td>
                      </tr>
                    ) : (
                      searchResults.map((a, i) => (
                        <tr key={i} className="border-t">
                          <td className="py-2 px-3 text-gray-900 font-medium">{a.reg}</td>
                          <td className="py-2 px-3 text-gray-900">{a.vehicle}</td>
                          <td className="py-2 px-3 text-gray-900">{a.ownerName || 'N/A'}</td>
                          <td className="py-2 px-3 text-gray-900">{a.date || '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )
        }

        {
          activeSection === 'upload' && (
            <UploadDocumentsSection
              user={user}
              selectedRegNumber={selectedRegNumber}
            />
          )
        }

        {
          activeSection === 'video-upload' && (
            <VideoUpload user={user} />
          )
        }

        {
          activeSection === 'tasks' && (
            <div className="space-y-8">
              {/* Assigned Jobs */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                    Assigned Jobs
                  </h2>
                  <button className="px-3 py-2 rounded-lg border hover:bg-gray-50" onClick={loadTasks}>Refresh</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-gray-900 font-bold">
                        <th className="text-left py-2 px-3">Reg Num</th>
                        <th className="text-left py-2 px-3">Customer Name</th>
                        <th className="text-left py-2 px-3">Address</th>
                        <th className="text-left py-2 px-3">Contact</th>
                        <th className="text-left py-2 px-3">Status</th>
                        <th className="text-left py-2 px-3">Deadline</th>
                        <th className="text-left py-2 px-3">Assigned By</th>
                        <th className="text-right py-2 px-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasksLoading ? (
                        <tr><td colSpan={8} className="py-6 text-center text-gray-500">Loading jobs…</td></tr>
                      ) : tasks.filter(t => t.status !== 'Completed').length === 0 ? (
                        <tr><td colSpan={8} className="py-6 text-center text-gray-500">No assigned jobs.</td></tr>
                      ) : (
                        tasks.filter(t => t.status !== 'Completed').map((t) => (
                          <tr key={t._id} className="border-t">
                            <td className="py-2 px-3 font-medium">
                              <div className="text-gray-900">{t.title}</div>
                              {t.description && <div className="text-xs text-gray-500">{t.description}</div>}
                            </td>
                            <td className="py-2 px-3 text-gray-900">
                              {t.customerName || '—'}
                            </td>
                            <td className="py-2 px-3 text-gray-900 text-xs" title={t.customerAddress}>
                              {t.customerAddress || '—'}
                            </td>
                            <td className="py-2 px-3 text-gray-900 text-xs">
                              {t.customerContact || '—'}
                            </td>
                            <td className="py-2 px-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${t.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                t.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                {t.status}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-gray-900">
                              {t.deadline ? new Date(t.deadline).toLocaleDateString() : "—"}
                            </td>
                            <td className="py-2 px-3 text-gray-900">
                              {t.assignedBy?.username || "Admin"}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {t.status !== 'Completed' && (
                                <div className="flex justify-end gap-2">
                                  {t.status === 'Pending' && (
                                    <button
                                      className="px-3 py-1 rounded-lg bg-blue-50 text-blue-600 text-xs"
                                      onClick={() => handleStartJob(t)}
                                    >
                                      Start
                                    </button>
                                  )}
                                  {t.status === 'In Progress' && (
                                    <button
                                      className="px-3 py-1 rounded-lg bg-green-50 text-green-600 text-xs"
                                      onClick={() => handleUpdateTaskStatus(t._id, 'Completed')}
                                    >
                                      Complete
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Completed Jobs */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-gray-500"></span>
                    Completed Jobs
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-gray-900 font-bold">
                        <th className="text-left py-2 px-3">Reg Num</th>
                        <th className="text-left py-2 px-3">Customer Name</th>
                        <th className="text-left py-2 px-3">Address</th>
                        <th className="text-left py-2 px-3">Contact</th>
                        <th className="text-left py-2 px-3">Status</th>
                        <th className="text-left py-2 px-3">Deadline</th>
                        <th className="text-left py-2 px-3">Assigned By</th>
                        <th className="text-right py-2 px-3">Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasksLoading ? (
                        <tr><td colSpan={8} className="py-6 text-center text-gray-500">Loading jobs…</td></tr>
                      ) : tasks.filter(t => t.status === 'Completed').length === 0 ? (
                        <tr><td colSpan={8} className="py-6 text-center text-gray-500">No completed jobs yet.</td></tr>
                      ) : (
                        tasks.filter(t => t.status === 'Completed').map((t) => (
                          <tr key={t._id} className="border-t">
                            <td className="py-2 px-3 font-medium">
                              <div className="text-gray-900">{t.title}</div>
                              {t.description && <div className="text-xs text-gray-500">{t.description}</div>}
                            </td>
                            <td className="py-2 px-3 text-gray-900">
                              {t.customerName || '—'}
                            </td>
                            <td className="py-2 px-3 text-gray-900 text-xs" title={t.customerAddress}>
                              {t.customerAddress || '—'}
                            </td>
                            <td className="py-2 px-3 text-gray-900 text-xs">
                              {t.customerContact || '—'}
                            </td>
                            <td className="py-2 px-3">
                              <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                {t.status}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-gray-900">
                              {t.deadline ? new Date(t.deadline).toLocaleDateString() : "—"}
                            </td>
                            <td className="py-2 px-3 text-gray-900">
                              {t.assignedBy?.username || "Admin"}
                            </td>
                            <td className="py-2 px-3 text-right text-green-600 text-xs font-semibold">
                              ✓ Done
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )
        }

        {
          message && (
            <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-white shadow-lg border rounded-lg px-6 py-3 text-sm animate-slideDown">
              {message}
            </div>
          )
        }
      </div >

      {
        pdfPreviewUrl && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => {
              if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
              setPdfPreviewUrl(null);
              setPdfFor(null);
            }}
          >
            <div className="bg-white rounded-xl w-full max-w-5xl h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-3 border-b">
                <div className="font-semibold">
                  {pdfFor?.reg ? `Document — ${pdfFor.reg}` : "Document"}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1 rounded-lg border hover:bg-gray-50"
                    onClick={() => pdfFor && generatePDF(pdfFor, { mode: "download" })}
                  >
                    Download
                  </button>
                  <button
                    className="px-3 py-1 rounded-lg border hover:bg-gray-50"
                    onClick={() => {
                      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
                      setPdfPreviewUrl(null);
                      setPdfFor(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
              <iframe className="flex-1" src={pdfPreviewUrl} title="Document Viewer" />
            </div>
          </div>
        )
      }

      {
        showRefreshWarning && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-md p-6 animate-slideDown">
              <h3 className="text-lg font-semibold mb-2 text-orange-600">⚠️ Page Refresh Detected</h3>
              <p className="text-gray-900 mb-4">
                The page was refreshed during an appraisal. Would you like to start a new appraisal from the beginning?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRefreshWarning(false);
                    if (user) {
                      localStorage.removeItem(`appraisalInProgress_${user.username}`);
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  No
                </button>
                <button
                  onClick={() => {
                    setShowRefreshWarning(false);
                    startNewAppraisal();
                    window.location.reload();
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                >
                  Yes, Start New
                </button>
              </div>
            </div>
          </div>
        )
      }

      {
        showManualPopup && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl w-full max-w-md p-6">
              <h3 className="text-lg font-semibold mb-2">Vehicle Data Not Found</h3>
              <p className="text-gray-900 mb-4">
                The vehicle data could not be retrieved automatically. Please enter the details manually.
              </p>
              <button
                onClick={() => setShowManualPopup(false)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Continue Manually
              </button>
            </div>
          </div>
        )
      }

      {/* Mobile Fullscreen Map Modal */}
      {
        mobileFullscreenMap && (
          <div className="mobile-fullscreen-overlay">
            <div className="mobile-fullscreen-header">
              <span className="mobile-fullscreen-title">
                {mobileFullscreenMap === 'exterior' ? '🚗 Exterior Inspection' : '🚙 Interior Inspection'}
              </span>
              <button
                className="mobile-fullscreen-close"
                onClick={() => setMobileFullscreenMap(null)}
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="mobile-fullscreen-hint">
              <p>👆 Scroll to view full image • Tap on image to mark faults</p>
            </div>

            <div className="mobile-fullscreen-content">
              {mobileFullscreenMap === 'exterior' ? (
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '20px' }}>
                  <img src={exteriorMobileImg} alt="Exterior View" style={{ maxWidth: '100%', height: 'auto' }} />
                </div>
              ) : (
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '20px' }}>
                  <img src={interiorMobileImg} alt="Interior View" style={{ maxWidth: '100%', height: 'auto' }} />
                </div>
              )}
            </div>

            <div className="mobile-fullscreen-footer">
              <button
                className="btn btn-done"
                onClick={() => setMobileFullscreenMap(null)}
                type="button"
              >
                ✓ Done ({mobileFullscreenMap === 'exterior' ? exteriorFaults.length : interiorFaults.length} faults)
              </button>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default StaffDashboard;
